/**
 * api.ts — ren API-klient for backend-kall
 *
 * Separation of concerns: denne filen håndterer KUN HTTP-kall mot backend.
 * Ingen state, ingen token-lagring — det gjøres i src/lib/auth.ts og
 * src/contexts/AuthContext.tsx.
 *
 * Konfigurasjon:
 * 1. API_URL — hentes fra NEXT_PUBLIC_API_URL (miljøvariabel)
 * 2. TENANT_ID — hardkodet til "1" per instruksjon i API.md
 *
 * Funksjoner:
 * 1. login — POST /api/auth/login med credentials + tenant_id
 * 2. fetchUsers — POST /api/sysadmin/iam/user ({ select: {} }) med JWT-token
 * 2c. updateUser — POST /api/sysadmin/iam/user ({ update: { data, where } }) med JWT-token
 * 3. fetchRoles — GET /api/sysadmin/iam/listroles med JWT-token
 * 3b. createRole — POST /api/sysadmin/iam/role med JWT-token
 * 3c. updateRole — POST /api/sysadmin/iam/role med JWT-token (update/where)
 * 3d. deleteRole — POST /api/sysadmin/iam/role med JWT-token (delete/where)
 * 4. fetchRolePermissions — POST /api/sysadmin/iam/listrolepermissionsreadable med JWT-token
 * 5. fetchUserPermissions — POST /api/sysadmin/iam/listuserpermissionsreadable med JWT-token
 * 6. createPermission — POST /api/sysadmin/iam/createpermission med JWT-token
 * 7. createRolePermission — POST /api/sysadmin/iam/createrolepermission med JWT-token (kjøres sist)
 * 8. fetchTuningTypes / createTuningType / deleteTuningType — POST /api/tuning/types
 * 9. fetchPriorityTypes / createPriorityType / deletePriorityType — POST /api/tuning/priority-types
 * 10. fetchTransmissionTypes / createTransmissionType / deleteTransmissionType — POST /api/tuning/transmission-types
 * 11. fetchToolTypes / createToolType / deleteToolType — POST /api/tuning/tool-types
 * 11d. fetchStatusTypes / createStatusType / deleteStatusType — POST /api/tuning/status-types
 * 12. fetchKjoretoy / createKjoretoy / deleteKjoretoy — POST /api/tuning/kjoretoy
 * 13. fetchForhandler / fetchForhandlerById / createForhandler / deleteForhandler — POST /api/sysadmin/tuning/forhandler
 * 14. assignUserForhandler / fetchUserForhandler / fetchForhandlerUsers — POST /api/sysadmin/tuning/user-forhandler
 * 15. fetchJobs — POST /api/tuning/job
 * 16. fetchStatensVegvesen — POST /api/external/statensvegvesen/enkeltoppslag
 * 17. fetchChatConversation / createChatConversation — POST /api/tuning/chat/conversations
 * 18. fetchChatMessages / createChatMessage — POST /api/tuning/chat/messages
 * 20. queryGoogleAds — POST /api/ext/google/ads (GAQL via Google Ads searchStream)
 * 21. fetchAiConversations — POST /api/ai/chat/conversations ({ select: {} })
 * 22. fetchAiMessages — POST /api/ai/chat/messages ({ select: { where: { chat_id } } })
 * 23. streamAiChat — POST /api/ai/chat (ny/fortsatt samtale, svar som SSE-strøm,
 *     body med model + provider)
 */

import type { LoginCredentials, LoginResponse } from '@/types/auth';
import type { RegisterUserInput, RegisterResponse } from '@/types/user';
import { getToken, clearToken } from '@/lib/auth';
import { getActiveForhandlerId } from '@/lib/active-forhandler';
import { normalizeIds } from '@/lib/ids';
import type { User } from '@/types/user';

import type { Role } from '@/types/role';
import type { RolePermission, Permission } from '@/types/role-permission';
import type { UserPermission } from '@/types/user-permission';
import type { Company } from '@/types/company';
import type {
  TuningType,
  PriorityType,
  TransmissionType,
  ToolType,
  StatusType,
} from '@/types/type';
import type { Kjoretoy, CreateKjoretoyInput } from '@/types/kjoretoy';
import type { Forhandler, CreateForhandlerInput } from '@/types/forhandler';
import type { Tenant } from '@/types/tenant';
import type { Job, JobViewRow, JobDetail, JobFile, CreateJobInput, UpdateJobInput } from '@/types/job';
import type { WooOrder } from '@/types/booking';
import type { ChatConversation, ChatMessage } from '@/types/chat';
import type { AiChatConversation, AiChatMessage, AiChatStreamHandlers } from '@/types/ai';

/**
 * 1. API_URL — base-URL for backend.
 * Leses fra miljøvariabel satt i .env.local.
 */
export const API_URL = process.env.NEXT_PUBLIC_API_URL;

/**
 * 2. TENANT_ID — hardkodet tenant per eksplisitt instruksjon i API.md.
 * Skal være "1" for nå. Når multi-tenant støttes, flytt til bruker-kontekst.
 */
export const TENANT_ID = '1';

/**
 * PAUSE: 502-redirects til /bad-gateway er slått AV (false).
 * Sett til true for å slå dem på igjen. Når av, gir 502 og nettverksfeil
 * APIets rå respons-body som feilmelding (via rawError).
 */
const REDIRECT_ON_BAD_GATEWAY = false;

/**
 * 3. handleBadGateway — redirect til /bad-gateway ved 502 fra backend.
 *
 * Kalles av alle fetch-kall når responsen er 502 Bad Gateway.
 * Siden-swap skjer via Next.js-ruting (klientnavigasjon).
 * Guard mot window: api.ts kan i prinsippet importeres i server-kontekst.
 */
function handleBadGateway(): void {
  if (!REDIRECT_ON_BAD_GATEWAY) return;
  if (typeof window !== 'undefined') {
    window.location.href = '/bad-gateway';
  }
}

/**
 * handleUnauthorized — redirect til /session-expired ved 401 fra backend.
 *
 * 401 betyr at JWT-tokenet er utløpt/ugyldig/revokert. Uansett årsak kan
 * ikke brukeren fortsette — vi sender hen til en tydelig beskjed-side.
 * Tokenet fjernes fra storage så ClientUiShell-garden ikke flimrer.
 *
 * Guard mot window: api.ts kan i prinsippet importeres i server-kontekst.
 */
function handleUnauthorized(): void {
  if (typeof window !== 'undefined') {
    clearToken();
    window.location.href = '/session-expired';
  }
}

/**
 * Sjekker en fetch-respons for 401 og redirecter i så fall.
 * Returnerer true hvis responsen var 401 (kallet skal da avbrytes).
 */
function isUnauthorized(response: Response): boolean {
  if (response.status === 401) {
    handleUnauthorized();
    return true;
  }
  return false;
}

/**
 * Sjekker en fetch-respons for 502 og redirecter i så fall.
 * Returnerer true hvis responsen var 502 (kallet skal da avbrytes).
 */
function isBadGateway(response: Response): boolean {
  if (REDIRECT_ON_BAD_GATEWAY && response.status === 502) {
    handleBadGateway();
    return true;
  }
  return false;
}

/**
 * Håndterer nettverksfeil fra fetch (TypeError: Failed to fetch).
 *
 * Nettverksfeil oppstår når backend er nede/ureachable — ofte manifesterer
 * dette seg som 502 Bad Gateway bak en reverse proxy. Vi redirecter til
 * /bad-gateway-siden i stedet for å vise en rå feilmelding.
 *
 * NB: Må kalles i catch-blokker rundt fetch-kall.
 */
function handleNetworkError(err: unknown): void {
  if (
    typeof window !== 'undefined' &&
    err instanceof TypeError &&
    err.message.includes('Failed to fetch')
  ) {
    handleBadGateway();
  }
}

/**
 * safeFetch — wrapper rundt fetch som fanger nettverksfeil sentralt.
 *
 * Ved TypeError (Failed to fetch — backend nede/ureachable) redirectes
 * brukeren til /bad-gateway. Feilen kastes videre så kall-koden håndterer
 * den som normalt (redirect skjer uansett).
 *
 * @param url - URL til fetch
 * @param init - fetch-options
 * @returns Response fra fetch
 */
async function safeFetch(url: string, init?: RequestInit): Promise<Response> {
  try {
    const response = await fetch(url, init);
    // ID-felter er string mot backend — tål number i responsen (se lib/ids.ts)
    const rawJson = response.json.bind(response);
    response.json = async () => normalizeIds(await rawJson());
    return response;
  } catch (err) {
    handleNetworkError(err);
    throw err;
  }
}

/**
 * rawError — bygger Error fra backendens rå respons-body (AS-IS).
 *
 * APIets respons er eneste source of truth: body returneres uendret,
 * uten prefix (HTTP-status), uten tolkning av JSON-felt og uten egen
 * tekst. Tom/uleselig body gir tom melding.
 *
 * @param response - non-2xx respons fra fetch
 * @returns Error med respons-bodyen som message
 */
async function rawError(response: Response): Promise<Error> {
  const body = await response.text().catch(() => '');
  return new Error(body);
}

/**
 * Rå bruker-respons fra POST /api/sysadmin/iam/user (snake_case).
 * Responsen er en array direkte (ikke innpakket i { data: [...] }).
 *
 * VIKTIG: Vi tolker IKKE role_id til UserRole. Backend bør eksponere
 * roleName (eller tilsvarende felt) direkte i responsen. Inntil det er
 * på plass, settes roleName til null.
 */
interface RawUser {
  user_id: string;
  username: string;
  email: string;
  user_type: string;
  role_id: string;
  role_name?: string;
  /** 1 = blokkert, 0 = aktiv */
  user_blocked: number;
  created_at: string;
  updated_at: string;
}

/**
 * NB: Vi tolker IKKE role_id til UserRole. Backend er kilde til sannhet.
 * For visning brukes User.roleName (som backend returnerer direkte).
 * Hvis backend ikke returnerer roleName ennå, er feltet null — da viser
 * vi bare role_id-nummeret til brukeren.
 *
 * For tilgangskontroll: bruk User.roleId (number) — aldri User.roleName.
 */

/**
 * Mapper rå backend-bruker (snake_case) til frontend User (camelCase).
 * user_blocked (1/0) → status 'suspended'/'active'.
 */
function mapRawUserToUser(raw: RawUser): User {
  return {
    id: String(raw.user_id),
    userId: raw.user_id,
    username: raw.username,
    name: raw.username, // Backend leverer ikke eget navn-felt — bruker username inntil videre
    email: raw.email,
    roleId: raw.role_id,
    roleName: raw.role_name ?? null,
    userType: raw.user_type,
    status: raw.user_blocked ? 'suspended' : 'active',
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
  };
}

/**
 * 1. login — autentiserer bruker mot backend
 *
 * Sender POST /api/auth/login med username, password og tenant_id.
 * Backend har Zod-validering, så vi returnerer responsen direkte.
 * Kaster Error ved non-2xx response (fetch-konvensjon).
 *
 * @param credentials - { username, password }
 * @returns LoginResponse - { token, user: { user_id, username } }
 * @throws Error ved nettverksfeil eller non-2xx response
 */
export async function login(credentials: LoginCredentials): Promise<LoginResponse> {
  const response = await safeFetch(`${API_URL}/api/auth/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      username: credentials.username,
      password: credentials.password,
    }),
  });

  if (isBadGateway(response)) {
    throw new Error('Bad Gateway — redirect til feilside pågår');
  }

  if (!response.ok) {
    throw await rawError(response);
  }

  return response.json();
}

/**
 * 1b. registerUser — oppretter ny bruker via /api/auth/register
 *
 * Sender POST /api/auth/register med username, email, password og
 * confirmPassword (verifisert kontrakt 2026-09-19).
 *
 * Krever JWT-token i Authorization-header (brukes fra /users som
 * admin-handling).
 * Ingen tenant_id i kontrakten — backend håndterer det selv.
 * Backend setter role_id (responsen viser role_id: 1 = guest).
 *
 * @param input - { username, email, password, confirmPassword }
 * @returns RegisterResponse - { user_id, tenant_id, username, email, role_id }
 * @throws Error ved nettverksfeil, manglende token eller non-2xx response
 */
export async function registerUser(
  input: RegisterUserInput
): Promise<RegisterResponse> {
  const token = getToken();
  if (!token) {
    throw new Error('Ingen JWT-token — bruker er ikke autentisert');
  }

  const response = await safeFetch(`${API_URL}/api/auth/register`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      username: input.username,
      email: input.email,
      password: input.password,
      confirmPassword: input.confirmPassword,
    }),
  });

  if (isUnauthorized(response)) {
    throw new Error('Unauthorized — redirect til /session-expired pågår');
  }

  if (isBadGateway(response)) {
    throw new Error('Bad Gateway — redirect til feilside pågår');
  }

  if (!response.ok) {
    throw await rawError(response);
  }

  return response.json();
}

/**
 * 2b. deleteUser — sletter en bruker
 *
 * Sender POST /api/sysadmin/iam/user med body (verifisert kontrakt
 * 2026-09-19):
 *   { "delete": { "where": { "user_id": "<id>" } } }
 *
 * Krever JWT-token (sysadmin-endpoint).
 *
 * @param userId - bruker-ID som skal slettes
 * @throws Error ved nettverksfeil, manglende token eller non-2xx response
 */
export async function deleteUser(userId: string): Promise<void> {
  const token = getToken();
  if (!token) {
    throw new Error('Ingen JWT-token — bruker er ikke autentisert');
  }

  const response = await safeFetch(`${API_URL}/api/sysadmin/iam/user`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      delete: {
        where: {
          user_id: String(userId),
        },
      },
    }),
  });

  if (isUnauthorized(response)) {
    throw new Error('Unauthorized — redirect til /session-expired pågår');
  }

  if (isBadGateway(response)) {
    throw new Error('Bad Gateway — redirect til feilside pågår');
  }

  if (!response.ok) {
    throw await rawError(response);
  }
}

/**
 * Felter som kan oppdateres på en bruker (snake_case, sendes AS-IS).
 */
export interface UpdateUserData {
  username?: string;
  email?: string;
  role_id?: string;
  /** 1 = blokkert, 0 = aktiv */
  user_blocked?: 0 | 1;
  user_type?: string;
}

/**
 * 2c. updateUser — oppdaterer en bruker
 *
 * Sender POST /api/sysadmin/iam/user med body:
 *   { "update": { "data": { username, email, role_id, user_blocked, user_type },
 *                 "where": { "user_id": "<userId>" } } }
 *
 * Krever JWT-token (sysadmin-endpoint).
 *
 * @param userId - bruker-ID som skal oppdateres
 * @param data - felter som skal endres
 * @throws Error ved nettverksfeil, manglende token eller non-2xx response
 */
export async function updateUser(userId: string, data: UpdateUserData): Promise<void> {
  const token = getToken();
  if (!token) {
    throw new Error('Ingen JWT-token — bruker er ikke autentisert');
  }

  const response = await safeFetch(`${API_URL}/api/sysadmin/iam/user`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      update: {
        data: {
          ...data,
          ...(data.role_id !== undefined && { role_id: String(data.role_id) }),
        },
        where: { user_id: String(userId) },
      },
    }),
  });

  if (isUnauthorized(response)) {
    throw new Error('Unauthorized — redirect til /session-expired pågår');
  }

  if (isBadGateway(response)) {
    throw new Error('Bad Gateway — redirect til feilside pågår');
  }

  if (!response.ok) {
    throw await rawError(response);
  }
}

/**
 * 2. fetchUsers — henter alle brukere fra backend
 *
 * Sender POST /api/sysadmin/iam/user med body { "select": {} } og JWT-token
 * i Authorization-header. Svaret er en array av brukere (snake_case), som
 * mappes til User-objekter (camelCase).
 *
 * @returns User[] — liste med brukere
 * @throws Error ved nettverksfeil, manglende token eller non-2xx response
 */
export async function fetchUsers(): Promise<User[]> {
  const token = getToken();
  if (!token) {
    throw new Error('Ingen JWT-token — bruker er ikke autentisert');
  }

  const response = await safeFetch(`${API_URL}/api/sysadmin/iam/user`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ select: {} }),
  });

  if (isUnauthorized(response)) {
    throw new Error('Unauthorized — redirect til /session-expired pågår');
  }

  if (isBadGateway(response)) {
    throw new Error('Bad Gateway — redirect til feilside pågår');
  }

  if (!response.ok) {
    throw await rawError(response);
  }

  const data: RawUser[] = await response.json();
  return data.map(mapRawUserToUser);
}

/**
 * Rå rolle-respons fra backend (snake_case).
 * Responsen er en array direkte (ikke innpakket i { data: [...] }).
 */
interface RawRole {
  role_id: string;
  role_name: string;
  role_description: string;
}

/**
 * Mapper rå backend-rolle (snake_case) til frontend Role (camelCase).
 */
function mapRawRoleToRole(raw: RawRole): Role {
  return {
    id: String(raw.role_id),
    roleId: raw.role_id,
    name: raw.role_name,
    description: raw.role_description,
  };
}

/**
 * 3. fetchRoles — henter alle roller fra backend
 *
 * Sender GET /api/sysadmin/iam/listroles med JWT-token i Authorization-header.
 * Returnerer mappet liste med Role-objekter (camelCase).
 *
 * @returns Role[] — liste med roller
 * @throws Error ved nettverksfeil, manglende token eller non-2xx response
 */
export async function fetchRoles(): Promise<Role[]> {
  const token = getToken();
  if (!token) {
    throw new Error('Ingen JWT-token — bruker er ikke autentisert');
  }

  const response = await safeFetch(`${API_URL}/api/sysadmin/iam/listroles`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (isUnauthorized(response)) {
    throw new Error('Unauthorized — redirect til /session-expired pågår');
  }

  if (isBadGateway(response)) {
    throw new Error('Bad Gateway — redirect til feilside pågår');
  }

  if (!response.ok) {
    throw await rawError(response);
  }

  const data: RawRole[] = await response.json();
  return data.map(mapRawRoleToRole);
}

/**
 * 3a. fetchTenants — henter alle tenants fra backend
 *
 * Kontrakt (brukerlevert 2026-10-02, AS-IS):
 * POST /api/sysadmin/iam/tenant med body
 *   { "select": { "select": ["tenant_id", "tenant_name", "tenant_org_number",
 *     "tenant_status", "tenant_created_at", "tenant_last_modified"] } }
 * og JWT-token i Authorization-header.
 * Responsen er en array av tenants (snake_case), returnert som den er.
 */
export async function fetchTenants(): Promise<Tenant[]> {
  const token = getToken();
  if (!token) {
    throw new Error('Ingen JWT-token — bruker er ikke autentisert');
  }

  const response = await safeFetch(`${API_URL}/api/sysadmin/iam/tenant`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      select: {
        select: [
          'tenant_id',
          'tenant_name',
          'tenant_org_number',
          'tenant_status',
          'tenant_created_at',
          'tenant_last_modified',
        ],
      },
    }),
  });

  if (isUnauthorized(response)) {
    throw new Error('Unauthorized — redirect til /session-expired pågår');
  }

  if (isBadGateway(response)) {
    throw new Error('Bad Gateway — redirect til feilside pågår');
  }

  if (!response.ok) {
    throw await rawError(response);
  }

  const data = await response.json();
  return (Array.isArray(data) ? data : (data.data ?? [])) as Tenant[];
}

/**
 * 3a2. createTenant — oppretter en ny tenant
 *
 * Kontrakt (brukerlevert 2026-10-02, AS-IS):
 * POST /api/sysadmin/iam/tenant med body
 *   { "insert": { "data": { "tenant_name", "tenant_org_number", "tenant_status" } } }
 * og JWT-token i Authorization-header.
 * Alle 3 felter er påkrevd. tenant_status godtar kun
 * 'active' | 'suspended' | 'terminated'.
 * Respons: opprettet Tenant (snake_case), returneres som den er.
 */
export const TENANT_STATUSES = ['active', 'suspended', 'terminated'] as const;
export type TenantStatus = (typeof TENANT_STATUSES)[number];

export interface CreateTenantInput {
  tenant_name: string;
  tenant_org_number: string;
  tenant_status: TenantStatus;
}

export async function createTenant(input: CreateTenantInput): Promise<Tenant> {
  const token = getToken();
  if (!token) {
    throw new Error('Ingen JWT-token — bruker er ikke autentisert');
  }

  const response = await safeFetch(`${API_URL}/api/sysadmin/iam/tenant`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ insert: { data: input } }),
  });

  if (isUnauthorized(response)) {
    throw new Error('Unauthorized — redirect til /session-expired pågår');
  }

  if (isBadGateway(response)) {
    throw new Error('Bad Gateway — redirect til feilside pågår');
  }

  if (!response.ok) {
    throw await rawError(response);
  }

  return (await response.json()) as Tenant;
}

/**
 * 3a3. deleteTenant — sletter en tenant
 *
 * Kontrakt (brukerlevert 2026-10-02, oppdatert etter backend-validering):
 * POST /api/sysadmin/iam/tenant med body
 *   { "delete": { "where": { "and": [
 *     { "tenant_id": "<uuid>" },
 *     { "tenant_org_number": "..." }
 *   ] } } }
 * og JWT-token i Authorization-header.
 * BEGGE feltene (tenant_id og tenant_org_number) er påkrevd — APIet
 * tillater kun én kolonne per where-nivå, så flere betingelser nests
 * via "and" (begge må matche).
 */
export async function deleteTenant(tenantId: string, tenantOrgNumber: string): Promise<void> {
  const token = getToken();
  if (!token) {
    throw new Error('Ingen JWT-token — bruker er ikke autentisert');
  }

  const response = await safeFetch(`${API_URL}/api/sysadmin/iam/tenant`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      delete: {
        where: {
          and: [
            { tenant_id: tenantId },
            { tenant_org_number: tenantOrgNumber },
          ],
        },
      },
    }),
  });

  if (isUnauthorized(response)) {
    throw new Error('Unauthorized — redirect til /session-expired pågår');
  }

  if (isBadGateway(response)) {
    throw new Error('Bad Gateway — redirect til feilside pågår');
  }

  if (!response.ok) {
    throw await rawError(response);
  }
}

/**
 * 3a4. updateTenant — oppdaterer en tenant
 *
 * Kontrakt (brukerlevert 2026-10-02, AS-IS):
 * POST /api/sysadmin/iam/tenant med body
 *   { "update": { "data": { "tenant_name", "tenant_org_number" },
 *                 "where": { "tenant_id": "<uuid>" } } }
 * og JWT-token i Authorization-header.
 * tenant_name og tenant_org_number er de ENESTE feltene som kan oppdateres.
 */
export interface UpdateTenantInput {
  tenant_name: string;
  tenant_org_number: string;
}

export async function updateTenant(tenantId: string, input: UpdateTenantInput): Promise<void> {
  const token = getToken();
  if (!token) {
    throw new Error('Ingen JWT-token — bruker er ikke autentisert');
  }

  const response = await safeFetch(`${API_URL}/api/sysadmin/iam/tenant`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      update: {
        data: input,
        where: {
          tenant_id: tenantId,
        },
      },
    }),
  });

  if (isUnauthorized(response)) {
    throw new Error('Unauthorized — redirect til /session-expired pågår');
  }

  if (isBadGateway(response)) {
    throw new Error('Bad Gateway — redirect til feilside pågår');
  }

  if (!response.ok) {
    throw await rawError(response);
  }
}

/**
 * 3b. createRole — oppretter en ny rolle
 *
 * Sender POST /api/sysadmin/iam/role med eksakt kontrakt (levert av bruker
 * 2026-09-22, brukes AS-IS uten generalisering):
 *
 *   {
 *     "insert": {
 *       "data": {
 *         "role_name": "mekaniker",
 *         "role_description": "Biltekniker med begrenset tilgang"
 *       }
 *     }
 *   }
 *
 * med JWT-token i Authorization-header.
 *
 * MERK: Kontrakten spesifiserer IKKE respons-format — derfor parses
 * respons-body IKKE. Kalleren skal re-hente rollelisten via fetchRoles()
 * etter vellykket opprettelse for å få backend-generert role_id.
 *
 * @param name - role_name (f.eks. "mekaniker")
 * @param description - role_description (f.eks. "Biltekniker med begrenset tilgang")
 * @throws Error ved nettverksfeil, manglende token eller non-2xx response
 */
export async function createRole(name: string, description: string): Promise<void> {
  const token = getToken();
  if (!token) {
    throw new Error('Ingen JWT-token — bruker er ikke autentisert');
  }

  const response = await safeFetch(`${API_URL}/api/sysadmin/iam/role`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      insert: {
        data: {
          role_name: name,
          role_description: description,
        },
      },
    }),
  });

  if (isUnauthorized(response)) {
    throw new Error('Unauthorized — redirect til /session-expired pågår');
  }

  if (isBadGateway(response)) {
    throw new Error('Bad Gateway — redirect til feilside pågår');
  }

  if (!response.ok) {
    throw await rawError(response);
  }
}

/**
 * 3c. updateRole — oppdaterer en eksisterende rolle
 *
 * Sender POST /api/sysadmin/iam/role med eksakt kontrakt (levert av bruker
 * 2026-09-22, brukes AS-IS uten generalisering). Kontrakten støtter tre
 * varianter — kun ENDEDE felt sendes i data:
 *
 *   { "update": { "data": { "role_name": "X" }, "where": { "role_id": X } } }
 *   { "update": { "data": { "role_description": "X" }, "where": { "role_id": X } } }
 *   { "update": { "data": { "role_name": "x", "role_description": "x" },
 *               "where": { "role_id": x } } }
 *
 * med JWT-token i Authorization-header.
 *
 * MERK: Kontrakten spesifiserer IKKE respons-format — derfor parses
 * respons-body IKKE. Kalleren skal re-hente rollelisten via fetchRoles()
 * etter vellykket oppdatering.
 *
 * @param roleId - role_id i where-klausulen (number)
 * @param data - felt som skal endres: role_name og/eller role_description.
 *               Felter som er undefined utelates fra body (kun endrede felt sendes).
 * @throws Error ved nettverksfeil, manglende token eller non-2xx response
 */
export async function updateRole(
  roleId: string,
  data: { name?: string; description?: string }
): Promise<void> {
  const token = getToken();
  if (!token) {
    throw new Error('Ingen JWT-token — bruker er ikke autentisert');
  }

  // Bygger data-objektet med KUN feltene som skal endres (kontrakt AS-IS:
  // role_name og/eller role_description — aldri tomme/undefined felter)
  const updateData: { role_name?: string; role_description?: string } = {};
  if (data.name !== undefined) {
    updateData.role_name = data.name;
  }
  if (data.description !== undefined) {
    updateData.role_description = data.description;
  }

  const response = await safeFetch(`${API_URL}/api/sysadmin/iam/role`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      update: {
        data: updateData,
        where: {
          role_id: String(roleId),
        },
      },
    }),
  });

  if (isUnauthorized(response)) {
    throw new Error('Unauthorized — redirect til /session-expired pågår');
  }

  if (isBadGateway(response)) {
    throw new Error('Bad Gateway — redirect til feilside pågår');
  }

  if (!response.ok) {
    throw await rawError(response);
  }
}

/**
 * 3d. deleteRole — sletter en rolle
 *
 * Sender POST /api/sysadmin/iam/role med eksakt kontrakt (levert av bruker
 * 2026-09-22, brukes AS-IS uten generalisering):
 *
 *   {
 *     "delete": {
 *       "where": { "role_id": X }
 *     }
 *   }
 *
 * med JWT-token i Authorization-header.
 *
 * MERK: Kontrakten spesifiserer IKKE respons-format — derfor parses
 * respons-body IKKE. Kalleren skal re-hente rollelisten via fetchRoles()
 * etter vellykket sletting.
 *
 * @param roleId - role_id i where-klausulen (number)
 * @throws Error ved nettverksfeil, manglende token eller non-2xx response
 */
export async function deleteRole(roleId: string): Promise<void> {
  const token = getToken();
  if (!token) {
    throw new Error('Ingen JWT-token — bruker er ikke autentisert');
  }

  const response = await safeFetch(`${API_URL}/api/sysadmin/iam/role`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      delete: {
        where: {
          role_id: String(roleId),
        },
      },
    }),
  });

  if (isUnauthorized(response)) {
    throw new Error('Unauthorized — redirect til /session-expired pågår');
  }

  if (isBadGateway(response)) {
    throw new Error('Bad Gateway — redirect til feilside pågår');
  }

  if (!response.ok) {
    throw await rawError(response);
  }
}

/**
 * Rå role-permission-respons fra backend (snake_case).
 * Responsen er en array direkte (ikke innpakket i { data: [...] }).
 */
interface RawRolePermission {
  tenant_id: string;
  role_id: string;
  role_name: string;
  permission_id: string;
  permission_action: string;
  permission_subject: string;
}

/**
 * Mapper rå backend role-permission (snake_case) til frontend RolePermission (camelCase).
 */
function mapRawRolePermissionToRolePermission(raw: RawRolePermission): RolePermission {
  return {
    id: String(raw.permission_id),
    tenantId: raw.tenant_id,
    roleId: raw.role_id,
    roleName: raw.role_name,
    permissionId: raw.permission_id,
    action: raw.permission_action,
    subject: raw.permission_subject,
  };
}

/**
 * 4. fetchRolePermissions — henter tillatelser for en spesifikk rolle
 *
 * Sender POST /api/sysadmin/iam/listrolepermissionsreadable med { role_id } i body
 * og JWT-token i Authorization-header.
 * Returnerer mappet liste med RolePermission-objekter (camelCase).
 *
 * @param roleId - rolle-ID (number) for rollen man vil hente tillatelser for
 * @returns RolePermission[] — liste med tillatelser
 * @throws Error ved nettverksfeil, manglende token eller non-2xx response
 */
export async function fetchRolePermissions(roleId: string): Promise<RolePermission[]> {
  const token = getToken();
  if (!token) {
    throw new Error('Ingen JWT-token — bruker er ikke autentisert');
  }

  const response = await safeFetch(`${API_URL}/api/sysadmin/iam/listrolepermissionsreadable`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      role_id: String(roleId),
    }),
  });

  if (isUnauthorized(response)) {
    throw new Error('Unauthorized — redirect til /session-expired pågår');
  }

  if (isBadGateway(response)) {
    throw new Error('Bad Gateway — redirect til feilside pågår');
  }

  if (!response.ok) {
    throw await rawError(response);
  }

  const data: RawRolePermission[] = await response.json();
  return data.map(mapRawRolePermissionToRolePermission);
}

/**
 * Rå user-permission-respons fra backend (snake_case).
 * Responsen er en array direkte (ikke innpakket i { data: [...] }).
 */
interface RawUserPermission {
  tenant_id: string;
  user_id: string;
  username: string;
  role_id: string;
  role_name: string;
  permission_id: string;
  permission_action: string;
  permission_subject: string;
}

/**
 * Mapper rå backend user-permission (snake_case) til frontend UserPermission (camelCase).
 */
function mapRawUserPermissionToUserPermission(raw: RawUserPermission): UserPermission {
  return {
    id: `${raw.user_id}-${raw.permission_id}`,
    tenantId: raw.tenant_id,
    userId: raw.user_id,
    username: raw.username,
    roleId: raw.role_id,
    roleName: raw.role_name,
    permissionId: raw.permission_id,
    action: raw.permission_action,
    subject: raw.permission_subject,
  };
}

/**
 * 5. fetchUserPermissions — henter brukere og deres tillatelser for en spesifikk rolle
 *
 * Sender POST /api/sysadmin/iam/listuserpermissionsreadable med { role_id } i body
 * og JWT-token i Authorization-header.
 * Returnerer mappet liste med UserPermission-objekter (camelCase).
 *
 * @param roleId - rolle-ID (number) for rollen man vil hente brukere for
 * @returns UserPermission[] — liste med brukere og deres tillatelser
 * @throws Error ved nettverksfeil, manglende token eller non-2xx response
 */
export async function fetchUserPermissions(roleId: string): Promise<UserPermission[]> {
  const token = getToken();
  if (!token) {
    throw new Error('Ingen JWT-token — bruker er ikke autentisert');
  }

  const response = await safeFetch(`${API_URL}/api/sysadmin/iam/listuserpermissionsreadable`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      role_id: String(roleId),
    }),
  });

  if (isUnauthorized(response)) {
    throw new Error('Unauthorized — redirect til /session-expired pågår');
  }

  if (isBadGateway(response)) {
    throw new Error('Bad Gateway — redirect til feilside pågår');
  }

  if (!response.ok) {
    throw await rawError(response);
  }

  const data: RawUserPermission[] = await response.json();
  return data.map(mapRawUserPermissionToUserPermission);
}

/**
 * Rå respons fra POST /api/sysadmin/iam/createpermission.
 * Backend returnerer permission_id i RAWJSON-responsen.
 */
interface CreatePermissionResponse {
  permission_id: string;
}

/**
 * 6. createPermission — oppretter en ny tillatelse (permission)
 *
 * Sender POST /api/sysadmin/iam/createpermission med { permission_action, permission_subject }
 * og JWT-token i Authorization-header.
 * Returnerer permission_id fra backend-responsen (RAWJSON).
 *
 * @param action - permission_action (f.eks. "manage")
 * @param subject - permission_subject (f.eks. "ListRoles")
 * @returns number — permission_id fra backend
 * @throws Error ved nettverksfeil, manglende token eller non-2xx response
 */
export async function createPermission(action: string, subject: string): Promise<string> {
  const token = getToken();
  if (!token) {
    throw new Error('Ingen JWT-token — bruker er ikke autentisert');
  }

  const response = await safeFetch(`${API_URL}/api/sysadmin/iam/createpermission`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      permission_action: action,
      permission_subject: subject,
    }),
  });

  if (isUnauthorized(response)) {
    throw new Error('Unauthorized — redirect til /session-expired pågår');
  }

  if (isBadGateway(response)) {
    throw new Error('Bad Gateway — redirect til feilside pågår');
  }

  if (!response.ok) {
    throw await rawError(response);
  }

  const data: CreatePermissionResponse = await response.json();
  return data.permission_id;
}

/**
 * 7. createRolePermission — knytter en tillatelse til en rolle
 *
 * Sender POST /api/sysadmin/iam/createrolepermission med { role_id, permission_id }
 * og JWT-token i Authorization-header.
 * MÅ kjøres sist (etter at permission er opprettet og permission_id er kjent).
 *
 * @param roleId - rolle-ID (number)
 * @param permissionId - permission-ID (number) fra createPermission
 * @throws Error ved nettverksfeil, manglende token eller non-2xx response
 */
export async function createRolePermission(
  roleId: string,
  permissionId: string
): Promise<void> {
  const token = getToken();
  if (!token) {
    throw new Error('Ingen JWT-token — bruker er ikke autentisert');
  }

  const response = await safeFetch(`${API_URL}/api/sysadmin/iam/createrolepermission`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      role_id: String(roleId),
      permission_id: String(permissionId),
    }),
  });

  if (isUnauthorized(response)) {
    throw new Error('Unauthorized — redirect til /session-expired pågår');
  }

  if (isBadGateway(response)) {
    throw new Error('Bad Gateway — redirect til feilside pågår');
  }

  if (!response.ok) {
    throw await rawError(response);
  }
}

/**
 * Rå permission-respons fra GET /api/sysadmin/iam/listpermissions (snake_case).
 * Verifisert kontrakt 2026-09-19.
 */
interface RawPermission {
  permission_id: string;
  tenant_id: string;
  permission_action: string;
  permission_subject: string;
}

/**
 * 7b. fetchPermissions — henter ALLE tillatelser
 *
 * Sender GET /api/sysadmin/iam/listpermissions med JWT-token i
 * Authorization-header (verifisert kontrakt 2026-09-19).
 * Returnerer mappet liste med Permission-objekter.
 *
 * @returns Permission[] — liste med alle tillatelser
 * @throws Error ved nettverksfeil, manglende token eller non-2xx response
 */
export async function fetchPermissions(): Promise<Permission[]> {
  const token = getToken();
  if (!token) {
    throw new Error('Ingen JWT-token — bruker er ikke autentisert');
  }

  const response = await safeFetch(`${API_URL}/api/sysadmin/iam/listpermissions`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (isUnauthorized(response)) {
    throw new Error('Unauthorized — redirect til /session-expired pågår');
  }

  if (isBadGateway(response)) {
    throw new Error('Bad Gateway — redirect til feilside pågår');
  }

  if (!response.ok) {
    throw await rawError(response);
  }

  const data: RawPermission[] = await response.json();
  return data.map((raw) => ({
    permissionId: raw.permission_id,
    tenantId: raw.tenant_id,
    action: raw.permission_action,
    subject: raw.permission_subject,
  }));
}

/**
 * Rå selskaps-respons fra backend (snake_case).
 * Feltnavn følger backend-schema: company_*.
 */
interface RawCompany {
  company_id: number;
  company_name: string;
  company_org_number: string;
  company_website: string;
  company_phone_number: string;
  company_industry: string;
  company_address: string;
  company_city: string;
  company_zip_code: string;
  company_country: string;
  company_created_at: string;
  company_last_modified: string;
}

/**
 * Mapper rå backend-selskap (snake_case) til frontend Company (camelCase).
 */
function mapRawCompanyToCompany(raw: RawCompany): Company {
  return {
    id: String(raw.company_id),
    companyId: raw.company_id,
    name: raw.company_name,
    orgNumber: raw.company_org_number,
    website: raw.company_website,
    phoneNumber: raw.company_phone_number,
    industry: raw.company_industry,
    address: raw.company_address,
    city: raw.company_city,
    zipCode: raw.company_zip_code,
    country: raw.company_country,
    createdAt: raw.company_created_at,
    updatedAt: raw.company_last_modified,
  };
}

/**
 * Normaliserer backend-respons til ett RawCompany-objekt.
 * Backend kan returnere enten en array ([RawCompany]), ett enkelt objekt
 * (RawCompany) eller et objekt pakket inn i { data: RawCompany }.
 * Returnerer null hvis ingen gyldig selskapsdata finnes (for å unngå
 * "Cannot read properties of undefined" ved mapping).
 */
function normalizeRawCompany(json: unknown): RawCompany | null {
  if (Array.isArray(json)) {
    return json.length > 0 ? (json[0] as RawCompany) : null;
  }
  if (json && typeof json === 'object') {
    const obj = json as Record<string, unknown>;
    if (obj.data && typeof obj.data === 'object') {
      return obj.data as RawCompany;
    }
    return json as RawCompany;
  }
  return null;
}

/**
 * 8. fetchCompanies — henter alle selskaper fra backend (CRM)
 *
 * Sender POST /api/crm/company med et "select"-objekt i body
 * (angir hvilke felt som skal returneres) og JWT-token i Authorization-header.
 * Returnerer mappet liste med Company-objekter (camelCase).
 *
 * @returns Company[] — liste med selskaper
 * @throws Error ved nettverksfeil, manglende token eller non-2xx response
 */
export async function fetchCompanies(): Promise<Company[]> {
  const token = getToken();
  if (!token) {
    throw new Error('Ingen JWT-token — bruker er ikke autentisert');
  }

  const response = await safeFetch(`${API_URL}/api/crm/company`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      select: {
        select: [
          'company_id',
          'company_name',
          'company_org_number',
          'company_website',
          'company_phone_number',
          'company_industry',
          'company_created_at',
          'company_last_modified',
        ],
      },
    }),
  });

  if (isUnauthorized(response)) {
    throw new Error('Unauthorized — redirect til /session-expired pågår');
  }

  if (isBadGateway(response)) {
    throw new Error('Bad Gateway — redirect til feilside pågår');
  }

  if (!response.ok) {
    throw await rawError(response);
  }

  const data: RawCompany[] = await response.json();
  return data.map(mapRawCompanyToCompany);
}

/**
 * 9. fetchCompanyById — henter ett selskap fra backend (CRM) via company_id
 *
 * Sender POST /api/crm/company med et "select"-objekt i body
 * (where-filter på company_id) og JWT-token i Authorization-header.
 * Backend returnerer en array med ett element ([RawCompany]).
 * Returnerer mappet Company-objekt (camelCase) eller null hvis ikke funnet.
 *
 * @param companyId - selskaps-ID (number) for selskapet man vil hente
 * @returns Company | null — selskapet, eller null hvis ingen treff
 * @throws Error ved nettverksfeil, manglende token eller non-2xx response
 */
export async function fetchCompanyById(companyId: number): Promise<Company | null> {
  const token = getToken();
  if (!token) {
    throw new Error('Ingen JWT-token — bruker er ikke autentisert');
  }

  const response = await safeFetch(`${API_URL}/api/crm/company`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      select: {
        where: { company_id: companyId },
      },
    }),
  });

  if (isUnauthorized(response)) {
    throw new Error('Unauthorized — redirect til /session-expired pågår');
  }

  if (isBadGateway(response)) {
    throw new Error('Bad Gateway — redirect til feilside pågår');
  }

  if (!response.ok) {
    throw await rawError(response);
  }

  const raw = normalizeRawCompany(await response.json());
  if (!raw) {
    return null;
  }
  return mapRawCompanyToCompany(raw);
}

/**
 * 10. deleteCompany — sletter ett selskap fra backend (CRM) via company_id
 *
 * Sender POST /api/crm/company med et "delete"-objekt i body
 * (where-filter på company_id) og JWT-token i Authorization-header.
 *
 * @param companyId - selskaps-ID (number) for selskapet som skal slettes
 * @throws Error ved nettverksfeil, manglende token eller non-2xx response
 */
export async function deleteCompany(companyId: number): Promise<void> {
  const token = getToken();
  if (!token) {
    throw new Error('Ingen JWT-token — bruker er ikke autentisert');
  }

  const response = await safeFetch(`${API_URL}/api/crm/company`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      delete: {
        where: { company_id: companyId },
      },
    }),
  });

  if (isUnauthorized(response)) {
    throw new Error('Unauthorized — redirect til /session-expired pågår');
  }

  if (isBadGateway(response)) {
    throw new Error('Bad Gateway — redirect til feilside pågår');
  }

  if (!response.ok) {
    throw await rawError(response);
  }
}

/**
 * Input for oppdatering av selskap (camelCase → snake_case i body).
 * Inneholder kun de feltene som kan redigeres fra CompanyProfile.
 * `company_id`, `company_created_at` og `company_last_modified` er readonly.
 */
export interface UpdateCompanyInput {
  name: string;
  orgNumber: string;
  website: string;
  phoneNumber: string;
  industry: string;
  address: string;
  city: string;
  zipCode: string;
  country: string;
}

/**
 * 10c. updateCompany — oppdaterer felter på et eksisterende selskap (CRM)
 *
 * Sender POST /api/crm/company med et "update"-objekt i body
 * (data-felt + where-filter på company_id) og JWT-token i Authorization-header.
 * Returnerer oppdatert selskap mappet til frontend Company (camelCase).
 *
 * @param companyId - selskaps-ID (number) for selskapet som skal oppdateres
 * @param input - nye feltverdier (camelCase) som sendes inn som snake_case i body
 * @returns Company — oppdatert selskap
 * @throws Error ved nettverksfeil, manglende token eller non-2xx response
 */
export async function updateCompany(
  companyId: number,
  input: UpdateCompanyInput
): Promise<Company> {
  const token = getToken();
  if (!token) {
    throw new Error('Ingen JWT-token — bruker er ikke autentisert');
  }

  const response = await safeFetch(`${API_URL}/api/crm/company`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      update: {
        data: {
          company_name: input.name,
          company_org_number: input.orgNumber,
          company_website: input.website,
          company_phone_number: input.phoneNumber,
          company_industry: input.industry,
          company_address: input.address,
          company_city: input.city,
          company_zip_code: input.zipCode,
          company_country: input.country,
        },
        where: { company_id: companyId },
      },
    }),
  });

  if (isUnauthorized(response)) {
    throw new Error('Unauthorized — redirect til /session-expired pågår');
  }

  if (isBadGateway(response)) {
    throw new Error('Bad Gateway — redirect til feilside pågår');
  }

  if (!response.ok) {
    throw await rawError(response);
  }

  const raw = normalizeRawCompany(await response.json());
  if (!raw) {
    throw new Error('Backend returnerte ikke gyldig selskapsdata ved oppdatering');
  }
  return mapRawCompanyToCompany(raw);
}

/**
 * 10b. deleteCompanies — sletter flere selskaper (tabellnivå / bulk)
 *
 * Gjenbruker den verifiserte enkelt-slett-kontrakten (deleteCompany) og
 * kjører én DELETE per id i parallell. Dette unngår å gjette en bulk-kontrakt
 * mot backend.
 *
 * @param companyIds - liste med selskaps-IDer (number) som skal slettes
 * @throws Error hvis én eller flere slettinger feiler (første feil kastes)
 */
export async function deleteCompanies(companyIds: number[]): Promise<void> {
  await Promise.all(companyIds.map((id) => deleteCompany(id)));
}

/**
 * Input for opprettelse av selskap (camelCase → snake_case i body).
 */
export interface CreateCompanyInput {
  name: string;
  orgNumber: string;
  website: string;
  phoneNumber: string;
  industry: string;
  address: string;
  city: string;
  zipCode: string;
  country: string;
}

/**
 * 11. createCompany — oppretter ett selskap i backend (CRM)
 *
 * Sender POST /api/crm/company med et "insert"-objekt i body
 * (data med company_*-felt) og JWT-token i Authorization-header.
 * Returnerer mappet Company-objekt (camelCase) fra backend-responsen.
 *
 * @param input - selskapsdata (CreateCompanyInput)
 * @returns Company — det nyopprettede selskapet
 * @throws Error ved nettverksfeil, manglende token eller non-2xx response
 */
export async function createCompany(input: CreateCompanyInput): Promise<Company> {
  const token = getToken();
  if (!token) {
    throw new Error('Ingen JWT-token — bruker er ikke autentisert');
  }

  const response = await safeFetch(`${API_URL}/api/crm/company`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      insert: {
        data: {
          company_name: input.name,
          company_org_number: input.orgNumber,
          company_website: input.website,
          company_phone_number: input.phoneNumber,
          company_industry: input.industry,
          company_address: input.address,
          company_city: input.city,
          company_zip_code: input.zipCode,
          company_country: input.country,
        },
      },
    }),
  });

  if (isUnauthorized(response)) {
    throw new Error('Unauthorized — redirect til /session-expired pågår');
  }

  if (isBadGateway(response)) {
    throw new Error('Bad Gateway — redirect til feilside pågår');
  }

  if (!response.ok) {
    throw await rawError(response);
  }

  const raw = normalizeRawCompany(await response.json());
  if (!raw) {
    throw new Error('Backend returnerte ikke gyldig selskapsdata ved opprettelse');
  }
  return mapRawCompanyToCompany(raw);
}

// ============================================================================
// Type-tabeller (tuning/priority/transmission/tool types)
// ============================================================================
//
// Alle fire bruker samme mønster:
// - List:  POST {endpoint}  med body { "select": {} }
// - Create: POST {endpoint} med body { "insert": { "data": { name, category } } }
//
// tenant_id i responsen mappes as-is fra backend — ingen fallback.

/**
 * 8. fetchTuningTypes — henter alle tuning-typer fra backend
 *
 * Sender POST /api/tuning/types med body { "select": {} } og JWT-token.
 * Returnerer mappet liste med TuningType-objekter.
 */
export async function fetchTuningTypes(): Promise<TuningType[]> {
  const token = getToken();
  if (!token) {
    throw new Error('Ingen JWT-token — bruker er ikke autentisert');
  }

  const response = await safeFetch(`${API_URL}/api/tuning/types`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ select: {} }),
  });

  if (isUnauthorized(response)) {
    throw new Error('Unauthorized — redirect til /session-expired pågår');
  }

  if (isBadGateway(response)) {
    throw new Error('Bad Gateway — redirect til feilside pågår');
  }

  if (!response.ok) {
    throw await rawError(response);
  }

  const data = await response.json();
  const list = Array.isArray(data) ? data : (data.data ?? []);
  return (list as TuningType[]).map((row) => ({
    id: String(row.tuning_type_id),
    tuning_type_id: row.tuning_type_id,
    tenant_id: row.tenant_id,
    tuning_type_name: row.tuning_type_name,
    tuning_type_category: row.tuning_type_category,
  }));
}

/**
 * 8b. createTuningType — oppretter en ny tuning-type
 *
 * Sender POST /api/tuning/types med body { "insert": { "data": { ... } } }.
 */
export async function createTuningType(name: string, category: string): Promise<TuningType> {
  const token = getToken();
  if (!token) {
    throw new Error('Ingen JWT-token — bruker er ikke autentisert');
  }

  const response = await safeFetch(`${API_URL}/api/tuning/types`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      insert: {
        data: {
          tuning_type_name: name,
          tuning_type_category: category,
        },
      },
    }),
  });

  if (isUnauthorized(response)) {
    throw new Error('Unauthorized — redirect til /session-expired pågår');
  }

  if (isBadGateway(response)) {
    throw new Error('Bad Gateway — redirect til feilside pågår');
  }

  if (!response.ok) {
    throw await rawError(response);
  }

  const row = await response.json();
  return {
    id: String(row.tuning_type_id),
    tuning_type_id: row.tuning_type_id,
    tenant_id: row.tenant_id,
    tuning_type_name: row.tuning_type_name ?? name,
    tuning_type_category: row.tuning_type_category ?? category,
  };
}

/**
 * 9. fetchPriorityTypes — henter alle prioritets-typer fra backend
 *
 * Sender POST /api/tuning/priority-types med body { "select": {} }.
 */
export async function fetchPriorityTypes(): Promise<PriorityType[]> {
  const token = getToken();
  if (!token) {
    throw new Error('Ingen JWT-token — bruker er ikke autentisert');
  }

  const response = await safeFetch(`${API_URL}/api/tuning/priority-types`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ select: {} }),
  });

  if (isUnauthorized(response)) {
    throw new Error('Unauthorized — redirect til /session-expired pågår');
  }

  if (isBadGateway(response)) {
    throw new Error('Bad Gateway — redirect til feilside pågår');
  }

  if (!response.ok) {
    throw await rawError(response);
  }

  const data = await response.json();
  const list = Array.isArray(data) ? data : (data.data ?? []);
  return (list as PriorityType[]).map((row) => ({
    id: String(row.priority_type_id),
    priority_type_id: row.priority_type_id,
    tenant_id: row.tenant_id,
    priority_type_name: row.priority_type_name,
    priority_type_category: row.priority_type_category,
  }));
}

/**
 * 9b. createPriorityType — oppretter en ny prioritets-type
 */
export async function createPriorityType(name: string, category: string): Promise<PriorityType> {
  const token = getToken();
  if (!token) {
    throw new Error('Ingen JWT-token — bruker er ikke autentisert');
  }

  const response = await safeFetch(`${API_URL}/api/tuning/priority-types`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      insert: {
        data: {
          priority_type_name: name,
          priority_type_category: category,
        },
      },
    }),
  });

  if (isUnauthorized(response)) {
    throw new Error('Unauthorized — redirect til /session-expired pågår');
  }

  if (isBadGateway(response)) {
    throw new Error('Bad Gateway — redirect til feilside pågår');
  }

  if (!response.ok) {
    throw await rawError(response);
  }

  const row = await response.json();
  return {
    id: String(row.priority_type_id),
    priority_type_id: row.priority_type_id,
    tenant_id: row.tenant_id,
    priority_type_name: row.priority_type_name ?? name,
    priority_type_category: row.priority_type_category ?? category,
  };
}

/**
 * 10. fetchTransmissionTypes — henter alle transmission-typer fra backend
 *
 * Sender POST /api/tuning/transmission-types med body { "select": {} }.
 */
export async function fetchTransmissionTypes(): Promise<TransmissionType[]> {
  const token = getToken();
  if (!token) {
    throw new Error('Ingen JWT-token — bruker er ikke autentisert');
  }

  const response = await safeFetch(`${API_URL}/api/tuning/transmission-types`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ select: {} }),
  });

  if (isUnauthorized(response)) {
    throw new Error('Unauthorized — redirect til /session-expired pågår');
  }

  if (isBadGateway(response)) {
    throw new Error('Bad Gateway — redirect til feilside pågår');
  }

  if (!response.ok) {
    throw await rawError(response);
  }

  const data = await response.json();
  const list = Array.isArray(data) ? data : (data.data ?? []);
  return (list as TransmissionType[]).map((row) => ({
    id: String(row.transmission_type_id),
    transmission_type_id: row.transmission_type_id,
    tenant_id: row.tenant_id,
    transmission_type_name: row.transmission_type_name,
    transmission_type_category: row.transmission_type_category,
  }));
}

/**
 * 10b. createTransmissionType — oppretter en ny transmission-type
 */
export async function createTransmissionType(name: string, category: string): Promise<TransmissionType> {
  const token = getToken();
  if (!token) {
    throw new Error('Ingen JWT-token — bruker er ikke autentisert');
  }

  const response = await safeFetch(`${API_URL}/api/tuning/transmission-types`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      insert: {
        data: {
          transmission_type_name: name,
          transmission_type_category: category,
        },
      },
    }),
  });

  if (isUnauthorized(response)) {
    throw new Error('Unauthorized — redirect til /session-expired pågår');
  }

  if (isBadGateway(response)) {
    throw new Error('Bad Gateway — redirect til feilside pågår');
  }

  if (!response.ok) {
    throw await rawError(response);
  }

  const row = await response.json();
  return {
    id: String(row.transmission_type_id),
    transmission_type_id: row.transmission_type_id,
    tenant_id: row.tenant_id,
    transmission_type_name: row.transmission_type_name ?? name,
    transmission_type_category: row.transmission_type_category ?? category,
  };
}

/**
 * 11. fetchToolTypes — henter alle tool-typer fra backend
 *
 * Sender POST /api/tuning/tool-types med body { "select": {} }.
 */
export async function fetchToolTypes(): Promise<ToolType[]> {
  const token = getToken();
  if (!token) {
    throw new Error('Ingen JWT-token — bruker er ikke autentisert');
  }

  const response = await safeFetch(`${API_URL}/api/tuning/tool-types`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ select: {} }),
  });

  if (isUnauthorized(response)) {
    throw new Error('Unauthorized — redirect til /session-expired pågår');
  }

  if (isBadGateway(response)) {
    throw new Error('Bad Gateway — redirect til feilside pågår');
  }

  if (!response.ok) {
    throw await rawError(response);
  }

  const data = await response.json();
  const list = Array.isArray(data) ? data : (data.data ?? []);
  return (list as ToolType[]).map((row) => ({
    id: String(row.tool_type_id),
    tool_type_id: row.tool_type_id,
    tenant_id: row.tenant_id,
    tool_type_name: row.tool_type_name,
    tool_type_category: row.tool_type_category,
  }));
}

/**
 * 11b. createToolType — oppretter en ny tool-type
 */
export async function createToolType(name: string, category: string): Promise<ToolType> {
  const token = getToken();
  if (!token) {
    throw new Error('Ingen JWT-token — bruker er ikke autentisert');
  }

  const response = await safeFetch(`${API_URL}/api/tuning/tool-types`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      insert: {
        data: {
          tool_type_name: name,
          tool_type_category: category,
        },
      },
    }),
  });

  if (isUnauthorized(response)) {
    throw new Error('Unauthorized — redirect til /session-expired pågår');
  }

  if (isBadGateway(response)) {
    throw new Error('Bad Gateway — redirect til feilside pågår');
  }

  if (!response.ok) {
    throw await rawError(response);
  }

  const row = await response.json();
  return {
    id: String(row.tool_type_id),
    tool_type_id: row.tool_type_id,
    tenant_id: row.tenant_id,
    tool_type_name: row.tool_type_name ?? name,
    tool_type_category: row.tool_type_category ?? category,
  };
}

/**
 * 8c. deleteTuningType — sletter en tuning-type
 *
 * Sender POST /api/tuning/types med body:
 *   { "delete": { "where": { "and": [{ tuning_type_id }, { tuning_type_name }] } } }
 *
 * Begge feltene kreves i where-and (per backend-konvensjon).
 */
export async function deleteTuningType(id: number, name: string): Promise<void> {
  const token = getToken();
  if (!token) {
    throw new Error('Ingen JWT-token — bruker er ikke autentisert');
  }

  const response = await safeFetch(`${API_URL}/api/tuning/types`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      delete: {
        where: {
          and: [
            { tuning_type_id: id },
            { tuning_type_name: name },
          ],
        },
      },
    }),
  });

  if (isUnauthorized(response)) {
    throw new Error('Unauthorized — redirect til /session-expired pågår');
  }

  if (isBadGateway(response)) {
    throw new Error('Bad Gateway — redirect til feilside pågår');
  }

  if (!response.ok) {
    throw await rawError(response);
  }
}

/**
 * 9c. deletePriorityType — sletter en prioritets-type
 */
export async function deletePriorityType(id: number, name: string): Promise<void> {
  const token = getToken();
  if (!token) {
    throw new Error('Ingen JWT-token — bruker er ikke autentisert');
  }

  const response = await safeFetch(`${API_URL}/api/tuning/priority-types`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      delete: {
        where: {
          and: [
            { priority_type_id: id },
            { priority_type_name: name },
          ],
        },
      },
    }),
  });

  if (isUnauthorized(response)) {
    throw new Error('Unauthorized — redirect til /session-expired pågår');
  }

  if (isBadGateway(response)) {
    throw new Error('Bad Gateway — redirect til feilside pågår');
  }

  if (!response.ok) {
    throw await rawError(response);
  }
}

/**
 * 10c. deleteTransmissionType — sletter en transmission-type
 */
export async function deleteTransmissionType(id: number, name: string): Promise<void> {
  const token = getToken();
  if (!token) {
    throw new Error('Ingen JWT-token — bruker er ikke autentisert');
  }

  const response = await safeFetch(`${API_URL}/api/tuning/transmission-types`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      delete: {
        where: {
          and: [
            { transmission_type_id: id },
            { transmission_type_name: name },
          ],
        },
      },
    }),
  });

  if (isUnauthorized(response)) {
    throw new Error('Unauthorized — redirect til /session-expired pågår');
  }

  if (isBadGateway(response)) {
    throw new Error('Bad Gateway — redirect til feilside pågår');
  }

  if (!response.ok) {
    throw await rawError(response);
  }
}

/**
 * 11c. deleteToolType — sletter en tool-type
 */
export async function deleteToolType(id: number, name: string): Promise<void> {
  const token = getToken();
  if (!token) {
    throw new Error('Ingen JWT-token — bruker er ikke autentisert');
  }

  const response = await safeFetch(`${API_URL}/api/tuning/tool-types`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      delete: {
        where: {
          and: [
            { tool_type_id: id },
            { tool_type_name: name },
          ],
        },
      },
    }),
  });

  if (isUnauthorized(response)) {
    throw new Error('Unauthorized — redirect til /session-expired pågår');
  }

  if (isBadGateway(response)) {
    throw new Error('Bad Gateway — redirect til feilside pågår');
  }

  if (!response.ok) {
    throw await rawError(response);
  }
}

// ============================================================================
// Status Types (jobbstatuser)
// ============================================================================

/**
 * 11d. fetchStatusTypes — henter alle status-typer fra backend
 *
 * Sender POST /api/tuning/status-types med body { "select": {} }.
 */
export async function fetchStatusTypes(): Promise<StatusType[]> {
  const token = getToken();
  if (!token) {
    throw new Error('Ingen JWT-token — bruker er ikke autentisert');
  }

  const response = await safeFetch(`${API_URL}/api/tuning/status-types`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ select: {} }),
  });

  if (isUnauthorized(response)) {
    throw new Error('Unauthorized — redirect til /session-expired pågår');
  }

  if (isBadGateway(response)) {
    throw new Error('Bad Gateway — redirect til feilside pågår');
  }

  if (!response.ok) {
    throw await rawError(response);
  }

  const data = await response.json();
  const list = Array.isArray(data) ? data : (data.data ?? []);
  return (list as StatusType[]).map((row) => ({
    id: String(row.status_type_id),
    status_type_id: row.status_type_id,
    tenant_id: row.tenant_id,
    status_type_name: row.status_type_name,
    status_type_category: row.status_type_category,
  }));
}

/**
 * 11e. createStatusType — oppretter en ny status-type
 */
export async function createStatusType(name: string, category: string): Promise<StatusType> {
  const token = getToken();
  if (!token) {
    throw new Error('Ingen JWT-token — bruker er ikke autentisert');
  }

  const response = await safeFetch(`${API_URL}/api/tuning/status-types`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      insert: {
        data: {
          status_type_name: name,
          status_type_category: category,
        },
      },
    }),
  });

  if (isUnauthorized(response)) {
    throw new Error('Unauthorized — redirect til /session-expired pågår');
  }

  if (isBadGateway(response)) {
    throw new Error('Bad Gateway — redirect til feilside pågår');
  }

  if (!response.ok) {
    throw await rawError(response);
  }

  const row = await response.json();
  return {
    id: String(row.status_type_id),
    status_type_id: row.status_type_id,
    tenant_id: row.tenant_id,
    status_type_name: row.status_type_name ?? name,
    status_type_category: row.status_type_category ?? category,
  };
}

/**
 * 11f. deleteStatusType — sletter en status-type
 */
export async function deleteStatusType(id: number, name: string): Promise<void> {
  const token = getToken();
  if (!token) {
    throw new Error('Ingen JWT-token — bruker er ikke autentisert');
  }

  const response = await safeFetch(`${API_URL}/api/tuning/status-types`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      delete: {
        where: {
          and: [
            { status_type_id: id },
            { status_type_name: name },
          ],
        },
      },
    }),
  });

  if (isUnauthorized(response)) {
    throw new Error('Unauthorized — redirect til /session-expired pågår');
  }

  if (isBadGateway(response)) {
    throw new Error('Bad Gateway — redirect til feilside pågår');
  }

  if (!response.ok) {
    throw await rawError(response);
  }
}

// ============================================================================
// Kjoretoy (kjøretøy)
// ============================================================================
//
// - List:   POST /api/tuning/kjoretoy  med body { "select": { "where": { "forhandler_id": "<aktiv>" } } }
// - Create: POST /api/tuning/kjoretoy  med body { "insert": { "data": { ... } } }
// - Delete: POST /api/tuning/kjoretoy  med body { "delete": { "where": { "and": [...] } } }

/**
 * 12. fetchKjoretoy — henter kjøretøy for den aktive forhandleren fra backend
 *
 * Sender POST /api/tuning/kjoretoy med body:
 *   { "select": { "where": { "forhandler_id": "<aktiv forhandler>" } } }
 *
 * forhandler_id hentes fra localStorage ("aktiv_forhandler") — satt av
 * brukerens valg i switcher-panelet (se src/lib/active-forhandler.ts).
 * Kaster feil hvis ingen forhandler er valgt — ingen fallback-verdi.
 */
export async function fetchKjoretoy(): Promise<Kjoretoy[]> {
  const token = getToken();
  if (!token) {
    throw new Error('Ingen JWT-token — bruker er ikke autentisert');
  }

  const activeForhandlerId = getActiveForhandlerId();
  if (!activeForhandlerId) {
    throw new Error('Ingen aktiv forhandler valgt');
  }

  const response = await safeFetch(`${API_URL}/api/tuning/kjoretoy`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      select: {
        where: {
          forhandler_id: String(activeForhandlerId),
        },
      },
    }),
  });

  if (isUnauthorized(response)) {
    throw new Error('Unauthorized — redirect til /session-expired pågår');
  }

  if (isBadGateway(response)) {
    throw new Error('Bad Gateway — redirect til feilside pågår');
  }

  if (!response.ok) {
    throw await rawError(response);
  }

  const data = await response.json();
  const list = Array.isArray(data) ? data : (data.data ?? []);
  return (list as Kjoretoy[]).map((row) => ({
    id: String(row.kjoretoy_id),
    kjoretoy_id: row.kjoretoy_id,
    tenant_id: row.tenant_id,
    forhandler_id: row.forhandler_id,
    tuning_job_id: row.tuning_job_id,
    kjoretoy_kjennemerke: row.kjoretoy_kjennemerke,
    kjoretoy_understellsnummer: row.kjoretoy_understellsnummer,
    kjoretoy_kjennemerkekategori: row.kjoretoy_kjennemerkekategori,
    kjoretoy_registrering_fomtidspunkt: row.kjoretoy_registrering_fomtidspunkt,
    kjoretoy_teknisk_motorkode: row.kjoretoy_teknisk_motorkode,
    kjoretoy_teknisk_slagvolum: row.kjoretoy_teknisk_slagvolum,
    kjoretoy_teknisk_maksnettoeffekt: row.kjoretoy_teknisk_maksnettoeffekt,
    kjoretoy_teknisk_girkassetype: row.kjoretoy_teknisk_girkassetype,
    kjoretoy_teknisk_drivaksel: row.kjoretoy_teknisk_drivaksel,
    kjoretoy_teknisk_typegodkjenningNrTekst: row.kjoretoy_teknisk_typegodkjenningNrTekst,
    kjoretoy_created_at: row.kjoretoy_created_at ?? '',
    kjoretoy_last_modified: row.kjoretoy_last_modified ?? '',
  }));
}

/**
 * 12b. createKjoretoy — oppretter et nytt kjøretøy
 */
export async function createKjoretoy(input: CreateKjoretoyInput): Promise<Kjoretoy> {
  const token = getToken();
  if (!token) {
    throw new Error('Ingen JWT-token — bruker er ikke autentisert');
  }

  const response = await safeFetch(`${API_URL}/api/tuning/kjoretoy`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      insert: {
        data: {
          forhandler_id: String(input.forhandler_id),
          tuning_job_id: String(input.tuning_job_id),
          kjoretoy_kjennemerke: input.kjoretoy_kjennemerke,
          kjoretoy_understellsnummer: input.kjoretoy_understellsnummer,
          kjoretoy_kjennemerkekategori: input.kjoretoy_kjennemerkekategori,
          kjoretoy_registrering_fomtidspunkt: input.kjoretoy_registrering_fomtidspunkt,
          kjoretoy_teknisk_motorkode: input.kjoretoy_teknisk_motorkode,
          kjoretoy_teknisk_slagvolum: input.kjoretoy_teknisk_slagvolum,
          kjoretoy_teknisk_maksnettoeffekt: input.kjoretoy_teknisk_maksnettoeffekt,
          kjoretoy_teknisk_girkassetype: input.kjoretoy_teknisk_girkassetype,
          kjoretoy_teknisk_drivaksel: input.kjoretoy_teknisk_drivaksel,
          kjoretoy_teknisk_typegodkjenningNrTekst: input.kjoretoy_teknisk_typegodkjenningNrTekst,
        },
      },
    }),
  });

  if (isUnauthorized(response)) {
    throw new Error('Unauthorized — redirect til /session-expired pågår');
  }

  if (isBadGateway(response)) {
    throw new Error('Bad Gateway — redirect til feilside pågår');
  }

  if (!response.ok) {
    throw await rawError(response);
  }

  const row = await response.json();
  return {
    id: String(row.kjoretoy_id),
    kjoretoy_id: row.kjoretoy_id,
    tenant_id: row.tenant_id,
    forhandler_id: row.forhandler_id ?? input.forhandler_id,
    tuning_job_id: row.tuning_job_id ?? input.tuning_job_id,
    kjoretoy_kjennemerke: row.kjoretoy_kjennemerke ?? input.kjoretoy_kjennemerke,
    kjoretoy_understellsnummer: row.kjoretoy_understellsnummer ?? input.kjoretoy_understellsnummer,
    kjoretoy_kjennemerkekategori: row.kjoretoy_kjennemerkekategori ?? input.kjoretoy_kjennemerkekategori,
    kjoretoy_registrering_fomtidspunkt: row.kjoretoy_registrering_fomtidspunkt ?? input.kjoretoy_registrering_fomtidspunkt,
    kjoretoy_teknisk_motorkode: row.kjoretoy_teknisk_motorkode ?? input.kjoretoy_teknisk_motorkode,
    kjoretoy_teknisk_slagvolum: row.kjoretoy_teknisk_slagvolum ?? input.kjoretoy_teknisk_slagvolum,
    kjoretoy_teknisk_maksnettoeffekt: row.kjoretoy_teknisk_maksnettoeffekt ?? input.kjoretoy_teknisk_maksnettoeffekt,
    kjoretoy_teknisk_girkassetype: row.kjoretoy_teknisk_girkassetype ?? input.kjoretoy_teknisk_girkassetype,
    kjoretoy_teknisk_drivaksel: row.kjoretoy_teknisk_drivaksel ?? input.kjoretoy_teknisk_drivaksel,
    kjoretoy_teknisk_typegodkjenningNrTekst: row.kjoretoy_teknisk_typegodkjenningNrTekst ?? input.kjoretoy_teknisk_typegodkjenningNrTekst,
    kjoretoy_created_at: row.kjoretoy_created_at ?? new Date().toISOString(),
    kjoretoy_last_modified: row.kjoretoy_last_modified ?? new Date().toISOString(),
  };
}

/**
 * 12c. deleteKjoretoy — sletter et kjøretøy
 */
export async function deleteKjoretoy(id: string, kjennemerke: string): Promise<void> {
  const token = getToken();
  if (!token) {
    throw new Error('Ingen JWT-token — bruker er ikke autentisert');
  }

  const response = await safeFetch(`${API_URL}/api/tuning/kjoretoy`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      delete: {
        where: {
          and: [
            { kjoretoy_id: String(id) },
            { kjoretoy_kjennemerke: kjennemerke },
          ],
        },
      },
    }),
  });

  if (isUnauthorized(response)) {
    throw new Error('Unauthorized — redirect til /session-expired pågår');
  }

  if (isBadGateway(response)) {
    throw new Error('Bad Gateway — redirect til feilside pågår');
  }

  if (!response.ok) {
    throw await rawError(response);
  }
}

// ============================================================================
// Forhandler
// ============================================================================
//
// - List:   POST /api/sysadmin/tuning/forhandler  med body { "select": {} }
// - Create: POST /api/sysadmin/tuning/forhandler  med body { "insert": { "data": { ... } } }
// - Delete: POST /api/sysadmin/tuning/forhandler  med body { "delete": { "where": { "and": [...] } } }

/**
 * 13. fetchForhandler — henter alle forhandlere fra backend
 */
export async function fetchForhandler(): Promise<Forhandler[]> {
  const token = getToken();
  if (!token) {
    throw new Error('Ingen JWT-token — bruker er ikke autentisert');
  }

  const response = await safeFetch(`${API_URL}/api/sysadmin/tuning/forhandler`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ select: {} }),
  });

  if (isUnauthorized(response)) {
    throw new Error('Unauthorized — redirect til /session-expired pågår');
  }

  if (isBadGateway(response)) {
    throw new Error('Bad Gateway — redirect til feilside pågår');
  }

  if (!response.ok) {
    throw await rawError(response);
  }

  const data = await response.json();
  const list = Array.isArray(data) ? data : (data.data ?? []);
  return (list as Forhandler[]).map((row) => ({
    id: String(row.forhandler_id),
    forhandler_id: row.forhandler_id,
    tenant_id: row.tenant_id,
    organisasjons_nummer: row.organisasjons_nummer,
    forhandler_navn: row.forhandler_navn,
    forhandler_created_at: row.forhandler_created_at ?? '',
    forhandler_last_modified: row.forhandler_last_modified ?? '',
  }));
}

/**
 * 13a. fetchForhandlerById — henter én forhandler basert på forhandler_id
 *
 * Sender POST /api/sysadmin/tuning/forhandler med body:
 *   { "select": { "where": { "forhandler_id": "<id>" } } }
 *
 * Returnerer null hvis forhandleren ikke finnes.
 */
export async function fetchForhandlerById(forhandlerId: string): Promise<Forhandler | null> {
  const token = getToken();
  if (!token) {
    throw new Error('Ingen JWT-token — bruker er ikke autentisert');
  }

  const response = await safeFetch(`${API_URL}/api/sysadmin/tuning/forhandler`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      select: {
        where: {
          forhandler_id: String(forhandlerId),
        },
      },
    }),
  });

  if (isUnauthorized(response)) {
    throw new Error('Unauthorized — redirect til /session-expired pågår');
  }

  if (isBadGateway(response)) {
    throw new Error('Bad Gateway — redirect til feilside pågår');
  }

  if (!response.ok) {
    throw await rawError(response);
  }

  const data = await response.json();
  const list = Array.isArray(data) ? data : (data.data ?? []);
  const row = list.length > 0 ? list[0] : null;
  if (!row) return null;

  return {
    id: String(row.forhandler_id),
    forhandler_id: row.forhandler_id,
    tenant_id: row.tenant_id,
    organisasjons_nummer: row.organisasjons_nummer,
    forhandler_navn: row.forhandler_navn,
    forhandler_created_at: row.forhandler_created_at ?? '',
    forhandler_last_modified: row.forhandler_last_modified ?? '',
  };
}

/**
 * 13b. createForhandler — oppretter en ny forhandler
 */
export async function createForhandler(input: CreateForhandlerInput): Promise<Forhandler> {
  const token = getToken();
  if (!token) {
    throw new Error('Ingen JWT-token — bruker er ikke autentisert');
  }

  const response = await safeFetch(`${API_URL}/api/sysadmin/tuning/forhandler`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      insert: {
        data: {
          forhandler_navn: input.forhandler_navn,
          organisasjons_nummer: input.organisasjons_nummer,
        },
      },
    }),
  });

  if (isUnauthorized(response)) {
    throw new Error('Unauthorized — redirect til /session-expired pågår');
  }

  if (isBadGateway(response)) {
    throw new Error('Bad Gateway — redirect til feilside pågår');
  }

  if (!response.ok) {
    throw await rawError(response);
  }

  const row = await response.json();
  return {
    id: String(row.forhandler_id),
    forhandler_id: row.forhandler_id,
    tenant_id: row.tenant_id,
    organisasjons_nummer: row.organisasjons_nummer ?? input.organisasjons_nummer,
    forhandler_navn: row.forhandler_navn ?? input.forhandler_navn,
    forhandler_created_at: row.forhandler_created_at ?? new Date().toISOString(),
    forhandler_last_modified: row.forhandler_last_modified ?? new Date().toISOString(),
  };
}

/**
 * 13c. deleteForhandler — sletter en forhandler
 */
export async function deleteForhandler(id: string, navn: string): Promise<void> {
  const token = getToken();
  if (!token) {
    throw new Error('Ingen JWT-token — bruker er ikke autentisert');
  }

  const response = await safeFetch(`${API_URL}/api/sysadmin/tuning/forhandler`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      delete: {
        where: {
          and: [
            { forhandler_id: String(id) },
            { forhandler_navn: navn },
          ],
        },
      },
    }),
  });

  if (isUnauthorized(response)) {
    throw new Error('Unauthorized — redirect til /session-expired pågår');
  }

  if (isBadGateway(response)) {
    throw new Error('Bad Gateway — redirect til feilside pågår');
  }

  if (!response.ok) {
    throw await rawError(response);
  }
}

// ============================================================================
// User-Forhandler kobling
// ============================================================================
//
// - Insert: POST /api/sysadmin/tuning/user-forhandler  med body { "insert": { "data": { forhandler_id, user_id } } }

/**
 * 14. assignUserForhandler — knytter en bruker til en forhandler
 *
 * Sender POST /api/sysadmin/tuning/user-forhandler med body:
 *   { "insert": { "data": { "forhandler_id": "<id>", "user_id": "<id>" } } }
 *
 * Returnerer raw-respons fra backend (kan være objekt med inserted_id e.l.).
 */
export async function assignUserForhandler(
  userId: string,
  forhandlerId: string
): Promise<unknown> {
  const token = getToken();
  if (!token) {
    throw new Error('Ingen JWT-token — bruker er ikke autentisert');
  }

  const response = await safeFetch(`${API_URL}/api/sysadmin/tuning/user-forhandler`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      insert: {
        data: {
          forhandler_id: String(forhandlerId),
          user_id: String(userId),
        },
      },
    }),
  });

  if (isUnauthorized(response)) {
    throw new Error('Unauthorized — redirect til /session-expired pågår');
  }

  if (isBadGateway(response)) {
    throw new Error('Bad Gateway — redirect til feilside pågår');
  }

  if (!response.ok) {
    throw await rawError(response);
  }

  return response.json();
}

/**
 * 14b. fetchUserForhandler — henter forhandler-koblinger for en gitt bruker
 *
 * Sender POST /api/sysadmin/tuning/user-forhandler med body:
 *   { "select": { "where": { "user_id": "<id>" } } }
 *
 * Returnerer liste med koblings-rader (user_id, forhandler_id, evt. created_at).
 */
export interface UserForhandlerLink {
  user_id: string;
  forhandler_id: string;
  created_at?: string;
}

export async function fetchUserForhandler(userId: string): Promise<UserForhandlerLink[]> {
  const token = getToken();
  if (!token) {
    throw new Error('Ingen JWT-token — bruker er ikke autentisert');
  }

  const response = await safeFetch(`${API_URL}/api/sysadmin/tuning/user-forhandler`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      select: {
        where: {
          user_id: String(userId),
        },
      },
    }),
  });

  if (isUnauthorized(response)) {
    throw new Error('Unauthorized — redirect til /session-expired pågår');
  }

  if (isBadGateway(response)) {
    throw new Error('Bad Gateway — redirect til feilside pågår');
  }

  if (!response.ok) {
    throw await rawError(response);
  }

  const data = await response.json();
  const list = Array.isArray(data) ? data : (data.data ?? []);
  return (list as UserForhandlerLink[]).map((row) => ({
    user_id: row.user_id,
    forhandler_id: row.forhandler_id,
    created_at: row.created_at,
  }));
}

/**
 * 14c. fetchForhandlerUsers — henter bruker-koblinger for en gitt forhandler
 *
 * Sender POST /api/sysadmin/tuning/user-forhandler med body:
 *   { "select": { "where": { "forhandler_id": "<id>" } } }
 *
 * Returnerer liste med koblings-rader (user_id, forhandler_id, evt. created_at).
 */
export async function fetchForhandlerUsers(forhandlerId: string): Promise<UserForhandlerLink[]> {
  const token = getToken();
  if (!token) {
    throw new Error('Ingen JWT-token — bruker er ikke autentisert');
  }

  const response = await safeFetch(`${API_URL}/api/sysadmin/tuning/user-forhandler`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      select: {
        where: {
          forhandler_id: String(forhandlerId),
        },
      },
    }),
  });

  if (isUnauthorized(response)) {
    throw new Error('Unauthorized — redirect til /session-expired pågår');
  }

  if (isBadGateway(response)) {
    throw new Error('Bad Gateway — redirect til feilside pågår');
  }

  if (!response.ok) {
    throw await rawError(response);
  }

  const data = await response.json();
  const list = Array.isArray(data) ? data : (data.data ?? []);
  return (list as UserForhandlerLink[]).map((row) => ({
    user_id: row.user_id,
    forhandler_id: row.forhandler_id,
    created_at: row.created_at,
  }));
}

// ============================================================================
// Jobs (tuning-jobber)
// ============================================================================
//
// - List:   POST /api/view/tuning/job  (VIEW — støtter KUN select)
//           med body { "select": { "select": [...], "where": { "forhandler_id": "<aktiv>" } } }
// - Create: POST /api/tuning/job  med body { "insert": { "data": { ... } } }

/**
 * 15. fetchJobs — henter tuning-jobber for den aktive forhandleren fra backend
 *
 * Sender POST /api/view/tuning/job (VIEW-tabell, kun select) med body:
 *   { "select": {
 *       "select": [
 *         "tuning_job_id", "forhandler_navn", "tuning_type_name",
 *         "priority_type_name", "transmission_type_name", "tool_type_name",
 *         "kjoretoy_understellsnummer", "kjoretoy_kjennemerke", "tuning_created_at"
 *       ],
 *       "where": { "forhandler_id": "<aktiv forhandler>" }
 *   } }
 *
 * forhandler_id hentes fra localStorage ("aktiv_forhandler") — satt av
 * brukerens valg i switcher-panelet (se src/lib/active-forhandler.ts).
 * Kaster feil hvis ingen forhandler er valgt — ingen fallback-verdi.
 */
export async function fetchJobs(): Promise<JobViewRow[]> {
  const token = getToken();
  if (!token) {
    throw new Error('Ingen JWT-token — bruker er ikke autentisert');
  }

  const activeForhandlerId = getActiveForhandlerId();
  if (!activeForhandlerId) {
    throw new Error('Ingen aktiv forhandler valgt');
  }

  const response = await safeFetch(`${API_URL}/api/view/tuning/job`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      select: {
        select: [
          'tuning_job_id',
          'forhandler_navn',
          'tuning_type_name',
          'priority_type_name',
          'transmission_type_name',
          'tool_type_name',
          'kjoretoy_understellsnummer',
          'kjoretoy_kjennemerke',
          'tuning_created_at',
          'status_type_id',
          'status_type_name',
          'status_type_category',
        ],
        where: {
          forhandler_id: String(activeForhandlerId),
        },
      },
    }),
  });

  if (isUnauthorized(response)) {
    throw new Error('Unauthorized — redirect til /session-expired pågår');
  }

  if (isBadGateway(response)) {
    throw new Error('Bad Gateway — redirect til feilside pågår');
  }

  if (!response.ok) {
    throw await rawError(response);
  }

  const data = await response.json();
  const list = Array.isArray(data) ? data : (data.data ?? []);
  return (list as JobViewRow[]).map((row) => ({
    tuning_job_id: row.tuning_job_id,
    forhandler_navn: row.forhandler_navn,
    tuning_type_name: row.tuning_type_name,
    priority_type_name: row.priority_type_name,
    transmission_type_name: row.transmission_type_name,
    tool_type_name: row.tool_type_name,
    kjoretoy_understellsnummer: row.kjoretoy_understellsnummer,
    kjoretoy_kjennemerke: row.kjoretoy_kjennemerke,
    tuning_created_at: row.tuning_created_at,
  }));
}

/**
 * 15a. fetchJobById — henter detaljer for ÉN tuning-jobb
 *
 * Sender POST /api/view/tuning/job (VIEW-tabell, kun select) med body:
 *   { "select": { "where": { "and": [
 *       { "forhandler_id": "<aktiv forhandler>" },
 *       { "tuning_job_id": "<jobId>" }
 *   ] } } }
 *
 * forhandler_id hentes fra localStorage ("aktiv_forhandler") — ingen fallback.
 * Returnerer første rad i responsen, eller null hvis jobben ikke finnes.
 */
export async function fetchJobById(jobId: string): Promise<JobDetail | null> {
  const token = getToken();
  if (!token) {
    throw new Error('Ingen JWT-token — bruker er ikke autentisert');
  }

  const activeForhandlerId = getActiveForhandlerId();
  if (!activeForhandlerId) {
    throw new Error('Ingen aktiv forhandler valgt');
  }

  const response = await safeFetch(`${API_URL}/api/view/tuning/job`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      select: {
        where: {
          and: [
            { forhandler_id: String(activeForhandlerId) },
            { tuning_job_id: String(jobId) },
          ],
        },
      },
    }),
  });

  if (isUnauthorized(response)) {
    throw new Error('Unauthorized — redirect til /session-expired pågår');
  }

  if (isBadGateway(response)) {
    throw new Error('Bad Gateway — redirect til feilside pågår');
  }

  if (!response.ok) {
    throw await rawError(response);
  }

  const data = await response.json();
  const list = Array.isArray(data) ? data : (data.data ?? []);
  if (list.length === 0) {
    return null;
  }
  return list[0] as JobDetail;
}

/**
 * 15b. createJob — oppretter en ny tuning-jobb
 *
 * Sender POST /api/tuning/job med body (verifisert kontrakt 2026-09-18):
 *   { "insert": { "data": {
 *       forhandler_id, tuning_type_id, priority_type_id,
 *       transmission_type_id, tool_type_id, tuning_job_comment
 *   } } }
 *
 * forhandler_id hentes fra localStorage ("aktiv_forhandler") — ingen fallback.
 * Returnerer den opprettede jobben mappet til frontend Job.
 */
export async function createJob(input: CreateJobInput): Promise<Job> {
  const token = getToken();
  if (!token) {
    throw new Error('Ingen JWT-token — bruker er ikke autentisert');
  }

  const activeForhandlerId = getActiveForhandlerId();
  if (!activeForhandlerId) {
    throw new Error('Ingen aktiv forhandler valgt');
  }

  const response = await safeFetch(`${API_URL}/api/tuning/job`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      insert: {
        data: {
          forhandler_id: String(activeForhandlerId),
          tuning_type_id: input.tuning_type_id,
          priority_type_id: input.priority_type_id,
          transmission_type_id: input.transmission_type_id,
          tool_type_id: input.tool_type_id,
          status_type_id: input.status_type_id,
          tuning_job_comment: input.tuning_job_comment,
        },
      },
    }),
  });

  if (isUnauthorized(response)) {
    throw new Error('Unauthorized — redirect til /session-expired pågår');
  }

  if (isBadGateway(response)) {
    throw new Error('Bad Gateway — redirect til feilside pågår');
  }

  if (!response.ok) {
    throw await rawError(response);
  }

  const row = await response.json();
  return {
    id: String(row.tuning_job_id),
    tuning_job_id: row.tuning_job_id,
    tenant_id: row.tenant_id,
    forhandler_id: row.forhandler_id,
    tuning_type_id: row.tuning_type_id,
    priority_type_id: row.priority_type_id,
    transmission_type_id: row.transmission_type_id,
    tool_type_id: row.tool_type_id,
    beskrivelse: row.tuning_job_comment,
    created_at: row.tuning_created_at ?? '',
    last_modified: row.tuning_last_modified,
  };
}

/**
 * updateJob — endrer én tuning-jobb
 *
 * Sender POST /api/tuning/job med body (verifisert kontrakt 2026-09-24):
 *   { "update": { "data": { ...kun endrede felter },
 *                 "where": { "and": [ { "tuning_job_id": "<id>" }, { "forhandler_id": "<id>" } ] } } }
 *
 * forhandler_id er jobbens forhandler (fra JobDetail) — ikke aktiv forhandler.
 */
export async function updateJob(
  tuningJobId: string,
  forhandlerId: string,
  data: UpdateJobInput
): Promise<void> {
  const token = getToken();
  if (!token) {
    throw new Error('Ingen JWT-token — bruker er ikke autentisert');
  }

  const response = await safeFetch(`${API_URL}/api/tuning/job`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      update: {
        data,
        // Backend: nøyaktig én kolonne per where-nivå → flere betingelser via and
        where: {
          and: [{ tuning_job_id: String(tuningJobId) }, { forhandler_id: String(forhandlerId) }],
        },
      },
    }),
  });

  if (isUnauthorized(response)) {
    throw new Error('Unauthorized — redirect til /session-expired pågår');
  }

  if (isBadGateway(response)) {
    throw new Error('Bad Gateway — redirect til feilside pågår');
  }

  if (!response.ok) {
    throw await rawError(response);
  }
}

// ============================================================================
// Chat (samtaler + meldinger knyttet til tuning-jobber)
// ============================================================================
//
// Kontrakter (verifisert 2026-09-22):
// - Sjekk samtale:  POST /api/tuning/chat/conversations
//     { "select": { "where": { "and": [
//         { "forhandler_id": X }, { "tuning_job_id": Y } ] } } }
//     → array (tom [] = ingen samtale)
// - Opprett samtale: POST /api/tuning/chat/conversations
//     { "insert": { "data": { forhandler_id, tuning_job_id,
//         chat_title: "Job Y" } } }
// - List meldinger:  POST /api/tuning/chat/messages
//     { "select": { "where": { "chat_id": X } } }
//     → array (kronologisk, eldste først, kun aktiv tråd fra leaf-peker)
// - Send melding:    POST /api/tuning/chat/messages
//     { "insert": { "data": { "chat_id": X, "message_content": "..." } } }
//
// VIKTIG: forhandler_id og tuning_job_id kommer fra JOBBEN (JobDetail),
// aldri fra localStorage. message_role mottas rått — ingen frontend-logikk.

/**
 * 17a. fetchChatConversation — sjekker om det finnes en samtale for en jobb
 *
 * Returnerer første rad (eller null hvis tom array). Race conditions ved
 * samtidig opprettelse er backends ansvar — ingen spesialhåndtering her.
 */
export async function fetchChatConversation(
  forhandlerId: string,
  jobId: string
): Promise<ChatConversation | null> {
  const token = getToken();
  if (!token) {
    throw new Error('Ingen JWT-token — bruker er ikke autentisert');
  }

  const response = await safeFetch(`${API_URL}/api/tuning/chat/conversations`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      select: {
        where: {
          and: [{ forhandler_id: String(forhandlerId) }, { tuning_job_id: String(jobId) }],
        },
      },
    }),
  });

  if (isUnauthorized(response)) {
    throw new Error('Unauthorized — redirect til /session-expired pågår');
  }

  if (isBadGateway(response)) {
    throw new Error('Bad Gateway — redirect til feilside pågår');
  }

  if (!response.ok) {
    throw await rawError(response);
  }

  const data = await response.json();
  const list = Array.isArray(data) ? data : (data.data ?? []);
  if (list.length === 0) {
    return null;
  }
  return list[0] as ChatConversation;
}

/**
 * 17b. createChatConversation — oppretter samtale for en jobb
 *
 * Kalles KUN når fetchChatConversation returnerte null (ingen samtale).
 * chat_title settes til "Job <tuning_job_id>" per kontrakt.
 */
export async function createChatConversation(
  forhandlerId: string,
  jobId: string
): Promise<ChatConversation> {
  const token = getToken();
  if (!token) {
    throw new Error('Ingen JWT-token — bruker er ikke autentisert');
  }

  const response = await safeFetch(`${API_URL}/api/tuning/chat/conversations`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      insert: {
        data: {
          forhandler_id: String(forhandlerId),
          tuning_job_id: String(jobId),
          chat_title: `Job ${jobId}`,
        },
      },
    }),
  });

  if (isUnauthorized(response)) {
    throw new Error('Unauthorized — redirect til /session-expired pågår');
  }

  if (isBadGateway(response)) {
    throw new Error('Bad Gateway — redirect til feilside pågår');
  }

  if (!response.ok) {
    throw await rawError(response);
  }

  return (await response.json()) as ChatConversation;
}

/**
 * 18a. fetchChatMessages — henter meldingene i en samtale
 *
 * Backend leverer allerede kronologisk rekkefølge (eldste først) og kun
 * den aktive tråden (fra leaf-pekeren og ned). Tom samtale → tom array.
 */
export async function fetchChatMessages(chatId: number): Promise<ChatMessage[]> {
  const token = getToken();
  if (!token) {
    throw new Error('Ingen JWT-token — bruker er ikke autentisert');
  }

  const response = await safeFetch(`${API_URL}/api/tuning/chat/messages`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      select: {
        where: { chat_id: chatId },
      },
    }),
  });

  if (isUnauthorized(response)) {
    throw new Error('Unauthorized — redirect til /session-expired pågår');
  }

  if (isBadGateway(response)) {
    throw new Error('Bad Gateway — redirect til feilside pågår');
  }

  if (!response.ok) {
    throw await rawError(response);
  }

  const data = await response.json();
  return (Array.isArray(data) ? data : (data.data ?? [])) as ChatMessage[];
}

/**
 * 18b. createChatMessage — lagrer én melding i en samtale
 *
 * Returnerer opprettet melding (message_id, parent_message_id, osv.).
 */
export async function createChatMessage(
  chatId: number,
  content: string
): Promise<ChatMessage> {
  const token = getToken();
  if (!token) {
    throw new Error('Ingen JWT-token — bruker er ikke autentisert');
  }

  const response = await safeFetch(`${API_URL}/api/tuning/chat/messages`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      insert: {
        data: {
          chat_id: chatId,
          message_content: content,
        },
      },
    }),
  });

  if (isUnauthorized(response)) {
    throw new Error('Unauthorized — redirect til /session-expired pågår');
  }

  if (isBadGateway(response)) {
    throw new Error('Bad Gateway — redirect til feilside pågår');
  }

  if (!response.ok) {
    throw await rawError(response);
  }

  return (await response.json()) as ChatMessage;
}

// ============================================================================
// Filopplasting (tuning-filer)
// ============================================================================
//
// - Upload: POST /api/tuning/files/upload (multipart/form-data)
//   Krevfelter: file, file_rolle, forhandler_id, tuning_job_id

/**
 * 17. uploadFile — laster opp én fil knyttet til en tuning-jobb
 *
 * Sender POST /api/tuning/files/upload som multipart/form-data med:
 *   - file: selve filen
 *   - file_rolle: "original" | "modifisert" (brukerens valg i dropdown)
 *   - forhandler_id: aktiv forhandler (localStorage "aktiv_forhandler")
 *   - tuning_job_id: ID fra createJob-responsen
 *
 * NB: Ingen manuell Content-Type — browser setter multipart-boundary selv.
 * forhandler_id hentes fra localStorage — ingen fallback.
 */
export async function uploadFile(
  file: File,
  fileRolle: string,
  tuningJobId: string
): Promise<void> {
  const token = getToken();
  if (!token) {
    throw new Error('Ingen JWT-token — bruker er ikke autentisert');
  }

  const activeForhandlerId = getActiveForhandlerId();
  if (!activeForhandlerId) {
    throw new Error('Ingen aktiv forhandler valgt');
  }

  const formData = new FormData();
  formData.append('file', file);
  formData.append('file_rolle', fileRolle);
  formData.append('forhandler_id', activeForhandlerId);
  formData.append('tuning_job_id', String(tuningJobId));

  const response = await safeFetch(`${API_URL}/api/tuning/files/upload`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: formData,
  });

  if (isUnauthorized(response)) {
    throw new Error('Unauthorized — redirect til /session-expired pågår');
  }

  if (isBadGateway(response)) {
    throw new Error('Bad Gateway — redirect til feilside pågår');
  }

  if (!response.ok) {
    throw await rawError(response);
  }
}

/**
 * 17b. fetchJobFiles — henter filer knyttet til en tuning-jobb
 *
 * Sender POST /api/tuning/files med body (verifisert kontrakt 2026-09-19):
 *   { "select": { "where": { "tuning_job_id": "<jobId>" } } }
 *
 * Returnerer array av JobFile-objekter (kan være tomt).
 */
export async function fetchJobFiles(jobId: string): Promise<JobFile[]> {
  const token = getToken();
  if (!token) {
    throw new Error('Ingen JWT-token — bruker er ikke autentisert');
  }

  const response = await safeFetch(`${API_URL}/api/tuning/files`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      select: {
        where: {
          tuning_job_id: String(jobId),
        },
      },
    }),
  });

  if (isUnauthorized(response)) {
    throw new Error('Unauthorized — redirect til /session-expired pågår');
  }

  if (isBadGateway(response)) {
    throw new Error('Bad Gateway — redirect til feilside pågår');
  }

  if (!response.ok) {
    throw await rawError(response);
  }

  const data = await response.json();
  const list = Array.isArray(data) ? data : (data.data ?? []);
  return list as JobFile[];
}

/**
 * 17c. downloadJobFile — laster ned én fil fra backend
 *
 * Sender POST /api/tuning/files/download med body (verifisert kontrakt
 * 2026-09-19):
 *   { "file_key": "tuning/1/2/<uuid>.bin" }
 *
 * Responsen er filinnholdet (blob). Filen lagres lokalt hos brukeren
 * med opprinnelig filnavn via en midlertidig nedlastingslenke.
 *
 * NB: Bruker rå fetch med try/catch (samme nettverksfeil-håndtering
 * som safeFetch) siden vi trenger blob-respons, ikke JSON.
 */
export async function downloadJobFile(
  fileKey: string,
  originalName: string
): Promise<void> {
  const token = getToken();
  if (!token) {
    throw new Error('Ingen JWT-token — bruker er ikke autentisert');
  }

  let response: Response;
  try {
    response = await fetch(`${API_URL}/api/tuning/files/download`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        file_key: fileKey,
      }),
    });
  } catch (err) {
    handleNetworkError(err);
    throw err;
  }

  if (isUnauthorized(response)) {
    throw new Error('Unauthorized — redirect til /session-expired pågår');
  }

  if (isBadGateway(response)) {
    throw new Error('Bad Gateway — redirect til feilside pågår');
  }

  if (!response.ok) {
    throw await rawError(response);
  }

  // Konverter responsen til blob og utløs nedlasting i nettleseren
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = originalName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * 17d. deleteJobFile — sletter én tuning-fil
 *
 * Sender POST /api/tuning/files med body (verifisert kontrakt 2026-09-19):
 *   { "delete": { "where": { "file_id": "<id>" } } }
 *
 * Samme endpoint som liste (POST /api/tuning/files), men med delete-operasjon.
 */
export async function deleteJobFile(fileId: string): Promise<void> {
  const token = getToken();
  if (!token) {
    throw new Error('Ingen JWT-token — bruker er ikke autentisert');
  }

  const response = await safeFetch(`${API_URL}/api/tuning/files`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      delete: {
        where: {
          file_id: String(fileId),
        },
      },
    }),
  });

  if (isUnauthorized(response)) {
    throw new Error('Unauthorized — redirect til /session-expired pågår');
  }

  if (isBadGateway(response)) {
    throw new Error('Bad Gateway — redirect til feilside pågår');
  }

  if (!response.ok) {
    throw await rawError(response);
  }
}

// ============================================================================
// Statens Vegvesen — kjøretøyoppslag
// ============================================================================
//
// Sender kjennemerke eller understellsnummer (VIN) til SVV-proxy.
// Returnerer rå respons fra Statens Vegvesen (se README for struktur).
//
// Payload-eksempler:
//   { "kjennemerke": "EB11111" }
//   { "understellsnummer": "WVWZZZ1JZ3W123456" }

/**
 * Rå respons fra SVV (forenklet — kun felt vi bruker).
 * Se /api/external/statensvegvesen/enkeltoppslag for full struktur.
 */
export interface StatensVegvesenRespons {
  kjoretoyId?: {
    kjennemerke?: string;
    understellsnummer?: string;
  };
  forstegangsregistrering?: {
    registrertForstegangNorgeDato?: string;
  };
  kjennemerke?: Array<{
    fomTidspunkt?: string;
    kjennemerke?: string;
    kjennemerkekategori?: string;
  }>;
  registrering?: {
    fomTidspunkt?: string;
  };
  godkjenning?: {
    tekniskGodkjenning?: {
      kjoretoyklassifisering?: {
        efTypegodkjenning?: {
          typegodkjenningNrTekst?: string;
        };
      };
      tekniskeData?: {
        generelt?: {
          handelsbetegnelse?: string[];
          merke?: Array<{ merke?: string }>;
        };
        motorOgDrivverk?: {
          girkassetype?: {
            kodeBeskrivelse?: string;
          };
          motor?: Array<{
            motorKode?: string;
            slagvolum?: number;
            drivstoff?: Array<{
              maksNettoEffekt?: number;
            }>;
          }>;
        };
        akslinger?: {
          akselGruppe?: Array<{
            akselListe?: {
              aksel?: Array<{
                drivAksel?: boolean;
              }>;
            };
          }>;
        };
      };
    };
  };
}

/**
 * 16. fetchStatensVegvesen — henter kjøretøydata fra SVV-proxy
 *
 * Sender POST /api/external/statensvegvesen/enkeltoppslag med body:
 *   { "kjennemerke": "EB11111" }  eller
 *   { "understellsnummer": "WVWZZZ1JZ3W123456" }
 */
export async function fetchStatensVegvesen(
  payload: { kjennemerke?: string; understellsnummer?: string }
): Promise<StatensVegvesenRespons> {
  const token = getToken();
  if (!token) {
    throw new Error('Ingen JWT-token — bruker er ikke autentisert');
  }

  const response = await safeFetch(`${API_URL}/api/external/statensvegvesen/enkeltoppslag`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  if (isUnauthorized(response)) {
    throw new Error('Unauthorized — redirect til /session-expired pågår');
  }

  if (isBadGateway(response)) {
    throw new Error('Bad Gateway — redirect til feilside pågår');
  }

  if (!response.ok) {
    throw await rawError(response);
  }

  const data = await response.json();
  // SVV kan returnere enten ett objekt eller en array
  const first = Array.isArray(data) ? data[0] : data;
  return first as StatensVegvesenRespons;
}

/**
 * 19. fetchWooOrders — henter booking-ordrer fra WooCommerce-proxy
 *
 * Sender POST /api/external/woocommerce/orders med body { "select": {} }.
 * Kontrakt (brukerlevert 2026-09-24): responsen er et array av
 * WooCommerce-ordre med line_items (OVABRW rental-meta).
 */
export async function fetchWooOrders(): Promise<WooOrder[]> {
  const token = getToken();
  if (!token) {
    throw new Error('Ingen JWT-token — bruker er ikke autentisert');
  }

  const response = await safeFetch(`${API_URL}/api/external/woocommerce/orders`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ select: {} }),
  });

  if (isUnauthorized(response)) {
    throw new Error('Unauthorized — redirect til /session-expired pågår');
  }

  if (isBadGateway(response)) {
    throw new Error('Bad Gateway — redirect til feilside pågår');
  }

  if (!response.ok) {
    throw await rawError(response);
  }

  const data = await response.json();
  return (Array.isArray(data) ? data : (data.data ?? [])) as WooOrder[];
}

/**
 * 20. queryGoogleAds — kjører en GAQL-spørring mot Google Ads via vår proxy
 *
 * Sender POST /api/ext/google/ads med body:
 *   { "customerId": "1234567890", "query": "SELECT ... FROM ..." }
 * customerId sendes i proxyens URL mot Google (ikke i body til Google).
 *
 * Svaret er uendret fra Google Ads searchStream: en array av batcher
 * [{ results: [...], fieldMask, requestId }]. Vi flater ut til én rad-liste.
 * Ved feil kastes Googles egen feilmelding (error.details[0].errors[0].message).
 */
export async function queryGoogleAds<T>(customerId: string, query: string): Promise<T[]> {
  const token = getToken();
  if (!token) {
    throw new Error('Ingen JWT-token — bruker er ikke autentisert');
  }

  const response = await safeFetch(`${API_URL}/api/ext/google/ads`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ customerId: customerId.replace(/\D/g, ''), query }),
  });

  if (isUnauthorized(response)) {
    throw new Error('Unauthorized — redirect til /session-expired pågår');
  }

  if (isBadGateway(response)) {
    throw new Error('Bad Gateway — redirect til feilside pågår');
  }

  if (!response.ok) {
    // APIets rå respons er eneste source of truth — kastes uendret
    throw await rawError(response);
  }

  const data = await response.json().catch(() => null);

  const batches: { results?: T[] }[] = Array.isArray(data) ? data : data ? [data] : [];
  return batches.flatMap((batch) => batch.results ?? []);
}

/**
 * 21. fetchAiConversations — henter innlogget brukers AI-samtaler
 *
 * Sender POST /api/ai/chat/conversations med body { "select": {} } og
 * JWT-token. Svaret er en array av samtaler (snake_case), returnert som den er.
 */
export async function fetchAiConversations(): Promise<AiChatConversation[]> {
  const token = getToken();
  if (!token) {
    throw new Error('Ingen JWT-token — bruker er ikke autentisert');
  }

  const response = await safeFetch(`${API_URL}/api/ai/chat/conversations`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ select: {} }),
  });

  if (isUnauthorized(response)) {
    throw new Error('Unauthorized — redirect til /session-expired pågår');
  }

  if (isBadGateway(response)) {
    throw new Error('Bad Gateway — redirect til feilside pågår');
  }

  if (!response.ok) {
    throw await rawError(response);
  }

  const data = await response.json();
  return (Array.isArray(data) ? data : (data.data ?? [])) as AiChatConversation[];
}

/**
 * 22. fetchAiMessages — henter meldingene i én AI-samtale
 *
 * Sender POST /api/ai/chat/messages med body
 *   { "select": { "where": { "chat_id": <chatId> } } }
 * og JWT-token. Svaret er en array av meldinger (snake_case), returnert som den er.
 */
export async function fetchAiMessages(chatId: number): Promise<AiChatMessage[]> {
  const token = getToken();
  if (!token) {
    throw new Error('Ingen JWT-token — bruker er ikke autentisert');
  }

  const response = await safeFetch(`${API_URL}/api/ai/chat/messages`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ select: { where: { chat_id: chatId } } }),
  });

  if (isUnauthorized(response)) {
    throw new Error('Unauthorized — redirect til /session-expired pågår');
  }

  if (isBadGateway(response)) {
    throw new Error('Bad Gateway — redirect til feilside pågår');
  }

  if (!response.ok) {
    throw await rawError(response);
  }

  const data = await response.json();
  return (Array.isArray(data) ? data : (data.data ?? [])) as AiChatMessage[];
}

/**
 * 23. streamAiChat — sender en melding og strømmer svaret (SSE)
 *
 * Sender POST /api/ai/chat med body (model og provider sendes alltid med):
 *   ny samtale:    { "message_content": "...", "model": AI_CHAT_MODEL,
 *                    "provider": AI_CHAT_PROVIDER }                              (ingen chat_id)
 *   fortsett chat: { "chat_id": 12, "message_content": "...", "model": AI_CHAT_MODEL,
 *                    "provider": AI_CHAT_PROVIDER }
 * Uten chat_id oppretter serveren en ny samtale.
 * Kontrakt (brukerlevert 2026-09-30, AS-IS):
 *   { "provider": "Ollama", "message_content": "...", "model": "...", "chat_id": 42 }
 *
 * Svaret er en SSE-strøm (fetch + ReadableStream, siden det er POST):
 * - chat.created                → { chat_id } (kun ny samtale) → handlers.onChatCreated
 * - response.output_text.delta  → tekstbit                     → handlers.onTextDelta
 * - response.reasoning_summary_text.delta / response.reasoning_text.delta
 *                               → tenketekst                   → handlers.onReasoningDelta
 * - chat.round                  → { round } ny runde           → handlers.onRound
 * - chat.tool                   → { name, round } verktøykall  → handlers.onToolCall
 * - chat.tool_result            → { name, round, isError }     → handlers.onToolResult
 * - chat.done                  → { chat_id, assistant_message_id } (svaret er lagret)
 * - error                       → kaster med meldingen fra hendelsen
 *
 * Returnerer hele svarteksten. Avbrytes via `signal` (AbortError kastes videre).
 */
export const AI_CHAT_MODEL = 'z-ai/glm-5.3-flash';
export const AI_CHAT_PROVIDER = 'openrouter';

export async function streamAiChat(
  input: { chatId: number | null; message: string },
  handlers: AiChatStreamHandlers,
  signal?: AbortSignal
): Promise<string> {
  const token = getToken();
  if (!token) {
    throw new Error('Ingen JWT-token — bruker er ikke autentisert');
  }

  const response = await safeFetch(`${API_URL}/api/ai/chat`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(
      input.chatId === null
        ? { message_content: input.message, model: AI_CHAT_MODEL, provider: AI_CHAT_PROVIDER }
        : { chat_id: input.chatId, message_content: input.message, model: AI_CHAT_MODEL, provider: AI_CHAT_PROVIDER }
    ),
    signal,
  });

  if (isUnauthorized(response)) {
    throw new Error('Unauthorized — redirect til /session-expired pågår');
  }

  if (isBadGateway(response)) {
    throw new Error('Bad Gateway — redirect til feilside pågår');
  }

  if (!response.ok || !response.body) {
    // APIets rå respons er eneste source of truth — kastes uendret
    throw await rawError(response);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let text = '';

  // arguments/output kommer som tekst (JSON) — objekter gjøres om til tekst
  const asText = (value: unknown): string | undefined =>
    typeof value === 'string' ? value : value == null ? undefined : JSON.stringify(value);

  // Én SSE-blokk: event-navn fra "event:" (eller "type" i JSON) + "data:" → JSON.
  // Returnerer true når strømmen er ferdig (chat.done).
  const handleBlock = (block: string): boolean => {
    let eventName = '';
    const dataLines: string[] = [];
    for (const line of block.split('\n')) {
      if (line.startsWith('event:')) eventName = line.slice(6).trim();
      else if (line.startsWith('data:')) dataLines.push(line.slice(5).trimStart());
    }
    const data = dataLines.join('\n');
    if (!data) return false;
    if (data === '[DONE]') return true;

    let payload: {
      type?: string;
      chat_id?: number | string;
      delta?: string;
      message?: string;
      name?: string;
      call_id?: string;
      arguments?: unknown;
      output?: unknown;
      round?: number | string;
      isError?: boolean;
      response?: { usage?: { input_tokens?: number; output_tokens?: number } | null };
      error?: string | { message?: string };
    };
    try {
      payload = JSON.parse(data);
    } catch {
      return false; // ufullstendig/ukjent linje — hopp over
    }

    switch (eventName || payload.type) {
      case 'chat.created': {
        const chatId = Number(payload.chat_id);
        if (Number.isFinite(chatId)) handlers.onChatCreated(chatId);
        return false;
      }
      case 'response.output_text.delta':
        if (payload.delta) {
          text += payload.delta;
          handlers.onTextDelta(payload.delta);
        }
        return false;
      case 'response.reasoning_summary_text.delta':
      case 'response.reasoning_text.delta':
        if (payload.delta) handlers.onReasoningDelta?.(payload.delta);
        return false;
      case 'chat.round':
        handlers.onRound?.(Number(payload.round));
        return false;
      case 'chat.tool':
        handlers.onToolCall?.({
          name: payload.name ?? '',
          round: Number(payload.round),
          callId: payload.call_id,
          arguments: asText(payload.arguments),
        });
        return false;
      case 'chat.tool_result':
        handlers.onToolResult?.({
          name: payload.name ?? '',
          round: Number(payload.round),
          isError: Boolean(payload.isError),
          callId: payload.call_id,
          output: asText(payload.output),
        });
        return false;
      case 'response.created':
        handlers.onResponseEvent?.({ status: 'created' });
        return false;
      case 'response.in_progress':
        handlers.onResponseEvent?.({ status: 'in_progress' });
        return false;
      case 'response.completed': {
        const usage = payload.response?.usage;
        handlers.onResponseEvent?.({
          status: 'completed',
          usage: usage
            ? { inputTokens: usage.input_tokens ?? 0, outputTokens: usage.output_tokens ?? 0 }
            : undefined,
        });
        return false;
      }
      case 'chat.done':
        return true;
      case 'error':
        throw new Error(
          (typeof payload.error === 'string' ? payload.error : payload.error?.message) ??
            payload.message ??
            'AI-tjenesten returnerte en feil'
        );
      default:
        return false;
    }
  };

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, '\n');
      let sep: number;
      while ((sep = buffer.indexOf('\n\n')) !== -1) {
        const block = buffer.slice(0, sep);
        buffer = buffer.slice(sep + 2);
        if (handleBlock(block)) return text;
      }
    }
    // Siste hendelse uten avsluttende blank linje
    if (buffer.trim()) handleBlock(buffer);
    return text;
  } finally {
    reader.cancel().catch(() => {});
  }
}
